import { OperationsPage } from "./OperationsPage";
import { RegistrationLookup, registrationStepLabel } from "./RegistrationLookup";
import { RuntimeRetentionNotice, type RetentionNotice } from "./RuntimeRetentionNotice";
import { RuntimeReclaimNotice, type ReclaimNotice } from "./RuntimeReclaimNotice";
import { AccessKeysPage } from "./AccessKeysPage";
import { BillingPage } from "./billing/BillingPage";
import { DiagnosticText } from "./DiagnosticText";
import {diagnosticErrorText} from "./diagnosticMessage";
import { UpgradeConfiguration } from "./UpgradeConfiguration";
import { CapacityDialog } from "./CapacityDialog";
import { FleetOperationDialog, TeamMoveProgress, type FleetAction } from "./FleetOperations";
import { MaintenanceRecovery, RuntimeOperationResult } from "./MaintenanceRecovery";
import { SandboxDevelopmentDatabases } from "./SandboxDevelopmentDatabases";
import { NodeStoragePage } from "./NodeStoragePages";
import {
  createContext,
  type FormEvent,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import {
  BrowserRouter,
  Link,
  NavLink,
  Navigate,
  Route,
  Routes,
  useLocation,
  useSearchParams
} from "react-router-dom";
import {
  APIError,
  type AdminUser,
  type AdminUserInput,
  type AuthSession,
  bootstrapAdministrator,
  changeOwnPassword,
  createAdminUser,
  type ComponentObservation,
  type ComponentStatus,
  type DashboardData,
  getSession,
  getSetupState,
	getIDSConfiguration,
	getSandboxRuntime,
	getWorkspaceStorage,
	getTeamRuntime,
	type IDSConfiguration,
	type IDSDataCenterInput,
	type IDSDirectoryPolicyInput,
	type IDSOperation,
	type LLMGatewayProvider,
	type IDSTeam,
	type IDSTeamSort,
	type IDSTeamRegistration,
	type SortDirection,
	type TeamRuntimeSnapshot,
	type SandboxRuntimeSnapshot,
	type SandboxRuntimeTask,
	type WorkspaceStorageBackend,
	type WorkspaceStorageConfiguration,
	type WorkspaceStorageSnapshot,
	keepAliveSandboxRuntime,
	listIDSOperations,
	listIDSTeamRegistrations,
	listIDSTeams,
	listTeamRuntimeOperations,
	listSandboxOperations,
	listSandboxRuntimeTasks,
	listLLMGatewayOperations,
	listLLMGatewayProviders,
  listAdminUsers,
  loginAdministrator,
  logoutAdministrator,
  loadDashboard,
  resetAdminUserPassword,
	retryIDSTeamRegistration,
	reconcileTeamRuntime,
	reclaimSandboxRuntime,
	setAdminUserState,
	updateIDSDirectoryPolicy,
	updateWorkspaceStorageConfiguration,
	upsertIDSDataCenter
} from "./api";
import { listGlobalUsers } from "./identityManagementApi";
import { PactureLogo } from "./PactureLogo";
import { LanguageSwitcher, type Translator, useI18n } from "./i18n";
import { LLMGatewayProviders } from "./LLMGatewayProviders";
import { GlobalUsersPage, IdentityProvidersPage } from "./IdentityManagementPages";
import { WebSearchPage } from "./WebSearchPage";
import { ImageGenerationPage } from "./ImageGenerationPage";
import { EmailManagementPage, ArtifactStoragePage } from "./PlatformServicesPages";
import { ApplicationManagementPage } from "./PublishManagementPages";
import { ReleaseCenterManagementPage } from "./PublishLifecyclePage";
import { MonitoringPage } from "./MonitoringPage";
import { AlertsPage } from "./AlertsPage";

const refreshIntervalMilliseconds = 30_000;
const sessionConfirmationIntervalMilliseconds = 100;
const sessionConfirmationAttempts = 50;

async function resumeSessionWithRetry(signal: AbortSignal | undefined, attempts: number): Promise<AuthSession> {
  let lastError: unknown;
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      return await getSession(signal);
    } catch (caught) {
      lastError = caught;
      if (!(caught instanceof APIError) || caught.status !== 401 || attempt === attempts - 1) throw caught;
      await abortableDelay(sessionConfirmationIntervalMilliseconds, signal);
    }
  }
  throw lastError;
}

function abortableDelay(milliseconds: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("The operation was aborted", "AbortError"));
      return;
    }
    const timer = window.setTimeout(resolve, milliseconds);
    signal?.addEventListener("abort", () => {
      window.clearTimeout(timer);
      reject(new DOMException("The operation was aborted", "AbortError"));
    }, { once: true });
  });
}

interface DashboardContextValue {
  data: DashboardData | null;
  loading: boolean;
  refreshing: boolean;
  error: string;
  refreshedAt: Date | null;
  refresh: () => void;
  session: AuthSession;
  setSession: (session: AuthSession) => void;
}

const DashboardContext = createContext<DashboardContextValue | null>(null);

type NavigationIconName = "search" | "overview" | "monitoring" | "team" | "sandbox" | "ids" | "model" | "cluster" | "admin" | "login" | "mail" | "storage" | "applications" | "release";

interface NavigationItemDefinition {
  to: string;
  label: string;
  description: string;
  icon: NavigationIconName;
  end?: boolean;
}

interface NavigationGroupDefinition {
  label: string;
  items: NavigationItemDefinition[];
}

function navigationGroups(t: Translator): NavigationGroupDefinition[] {
  return [
  {
    label: t("运行状态", "Operations"),
    items: [
      { to: "/", label: t("数据中心总览", "Data center overview"), description: t("组件健康与运行状态", "Component health and runtime status"), icon: "overview", end: true },
      { to: "/monitoring", label: t("监控中心", "Monitoring center"), description: t("运行健康、执行性能与资源用量", "Runtime health, execution performance, and resource use"), icon: "monitoring" },
      { to: "/billing", label: t("费用与点数", "Billing & points"), description: t("团队点数、费用和计价规则", "Team points, costs and pricing"), icon: "monitoring" },
    { to: "/alerts", label: t("告警中心", "Alert center"), description: t("告警事件、规则与通知", "Alert events, rules, and notifications"), icon: "monitoring" }
    ]
  },
  {
    label: t("Pacture 运行面", "Pacture runtime"),
    items: [
      { to: "/team-runtime", label: "Team Runtime", description: t("实例、归属与路由", "Instances, placement, and routing"), icon: "team" },
      { to: "/sandbox", label: "Sandbox", description: t("运行实例与生命周期", "Runtime instances and lifecycle"), icon: "sandbox" },
      { to: "/workspace-storage", label: t("工作区存储", "Workspace Storage"), description: t("Provider、挂载与存储系统", "Providers, mounts, and storage systems"), icon: "sandbox" },
      { to: "/applications", label: t("应用管理", "Applications"), description: t("已发布应用与运行状态", "Published applications and runtime status"), icon: "applications" },
      { to: "/release-center", label: t("团队应用", "Team apps"), description: t("发布域名、证书与数据库", "Publishing domains, certificates, and databases"), icon: "release" }
    ]
  },
  {
    label: t("平台服务", "Platform services"),
    items: [
      { to: "/ids", label: "Identity Directory", description: t("身份目录与数据中心", "Identity directory and data centers"), icon: "ids" },
      { to: "/global-users", label: t("全局用户", "Global users"), description: t("用户身份与账号管理", "User identity and account management"), icon: "team" },
      { to: "/identity-providers", label: t("第三方登录", "Third-party login"), description: t("登录渠道与应用配置", "Login providers and application settings"), icon: "login" },
      { to: "/llm-gateway", label: "LLM Gateway", description: t("模型入口与健康状态", "Model gateway and health"), icon: "model" },
      { to: "/web-search", label: t("联网搜索", "Web search"), description: t("搜索服务、密钥与连接测试", "Search provider, credentials, and connection test"), icon: "search" },
      { to: "/image-generation", label: t("生图模型", "Image generation"), description: t("Tencent Hy Image 3.5 与按张计费", "Tencent Hy Image 3.5 and per-image pricing"), icon: "model" },
      { to: "/email", label: t("邮件管理", "Email"), description: t("邮件服务与投递记录", "Mail service and delivery history"), icon: "mail" }
    ]
  },
  {
    label: t("基础设施", "Infrastructure"),
    items: [
      { to: "/cluster", label: "Kubernetes", description: t("集群与节点", "Cluster and nodes"), icon: "cluster" },
      { to: "/artifact-storage", label: t("文件存储", "File storage"), description: t("存储配置与连通性检查", "Storage configuration and connection checks"), icon: "storage" },
      { to: "/node-storage", label: t("节点存储", "Node storage"), description: t("数据盘观测与分配授权", "Data disk observation and assignment"), icon: "storage" }
    ]
  },
  {
    label: t("系统管理", "System administration"),
    items: [
      { to: "/operations", label: t("管理操作", "Management operations"), description: t("检索操作记录及结果", "Search operations and outcomes"), icon: "admin" },
      { to: "/access-keys", label: t("访问密钥", "Access keys"), description: t("Agent 与自动化调用", "Agent and automation access"), icon: "admin" },
      { to: "/administrators", label: t("管理员", "Administrators"), description: t("账号与审计身份", "Accounts and audit identity"), icon: "admin" }
    ]
  }
  ];
}

export default function App() {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [authMode, setAuthMode] = useState<"loading" | "setup" | "login">("loading");

  useEffect(() => {
    const controller = new AbortController();
    resumeSessionWithRetry(controller.signal, 2)
      .then((result) => {
        setSession(result);
      })
      .catch(async (caught: unknown) => {
        if (caught instanceof DOMException && caught.name === "AbortError") return;
        if (!(caught instanceof APIError) || caught.status !== 401) {
          setAuthMode("login");
          return;
        }
        try {
          const setupRequired = await getSetupState(controller.signal);
          setAuthMode(setupRequired ? "setup" : "login");
        } catch (setupError) {
          if (setupError instanceof DOMException && setupError.name === "AbortError") return;
          setAuthMode("login");
        }
      });
    return () => controller.abort();
  }, []);

  const unauthenticated = useCallback(() => {
    setSession(null);
    setAuthMode("login");
  }, []);

  if (!session) {
    if (authMode === "loading") return <AuthenticationLoading />;
    return <AuthenticationPage mode={authMode} onAuthenticated={setSession} />;
  }

  if (session.user.must_change_password) {
    return <PasswordChangePage session={session} onChanged={setSession} onLogout={unauthenticated} />;
  }

  return (
    <DashboardProvider session={session} setSession={setSession} onUnauthenticated={unauthenticated}>
      <BrowserRouter basename="/control-center">
        <Shell onUnauthenticated={unauthenticated} />
      </BrowserRouter>
    </DashboardProvider>
  );
}

function AuthenticationPage({
  mode,
  onAuthenticated
}: {
  mode: "setup" | "login";
  onAuthenticated: (session: AuthSession) => void;
}) {
  const { t } = useI18n();
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [bootstrapToken, setBootstrapToken] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    resumeSessionWithRetry(controller.signal, 2)
      .then(onAuthenticated)
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === "AbortError") return;
        // Remaining on the login form is the expected unauthenticated state.
      });
    return () => controller.abort();
  }, [onAuthenticated]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!username.trim() || !password) {
      setError(t("请输入管理员用户名和密码", "Enter the administrator username and password"));
      return;
    }
    if (mode === "setup" && !displayName.trim()) {
      setError(t("请输入管理员显示名称", "Enter the administrator display name"));
      return;
    }
    if (mode === "setup" && password !== passwordConfirmation) {
      setError(t("两次输入的密码不一致", "The passwords do not match"));
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const issuedSession = mode === "setup"
        ? await bootstrapAdministrator(bootstrapToken, {
          username: username.trim(),
          display_name: displayName.trim(),
          password
        })
        : await loginAdministrator(username.trim(), password);
      const confirmedSession = await resumeSessionWithRetry(undefined, sessionConfirmationAttempts);
      onAuthenticated({ ...confirmedSession, user: issuedSession.user });
    } catch (caught) {
      setError(authenticationError(caught, t));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="connection-page">
      <section className="connection-intro" aria-labelledby="connection-title">
        <div className="connection-language"><LanguageSwitcher inverse /></div>
        <PactureLogo className="brand-mark" />
        <p className="eyebrow">ONES PACTURE</p>
        <h1 id="connection-title">Data Center<br />Control Center</h1>
        <p className="connection-copy">
          {t("一个数据中心，一个明确的管理面。查看集群、运行组件和基础设施状态，所有信息均来自真实管理 API。", "One data center, one clear control surface. Inspect the cluster, runtime components, and infrastructure using live management APIs.")}
        </p>
        <div className="connection-features" aria-label={t("首版能力", "Initial capabilities") }>
          <span>Cluster health</span>
          <span>LLM Gateway</span>
          <span>Component status</span>
        </div>
      </section>
      <section className="connection-card" aria-label={mode === "setup" ? t("创建首位管理员", "Create the first administrator") : t("管理员登录", "Administrator sign in")}>
        <div className="connection-card-header">
          <span className="status-dot status-dot-neutral" />
          <span>{mode === "setup" ? "INITIAL ADMINISTRATOR SETUP" : "ADMINISTRATOR SIGN IN"}</span>
        </div>
        <h2>{mode === "setup" ? t("创建首位管理员", "Create the first administrator") : t("登录 Control Center", "Sign in to Control Center")}</h2>
        <p>{mode === "setup"
          ? t("当前数据中心尚未创建管理员。bootstrap token 只用于这一次初始化。", "This data center has no administrator yet. The bootstrap token is used only for this initialization.")
          : t("使用 Control Center 自有管理员账号登录。会话由服务器管理，不在浏览器存储登录凭证。", "Sign in with a Control Center administrator account. The server manages the session; credentials are not stored in the browser.")}</p>
        <form onSubmit={submit}>
          {mode === "setup" ? (
            <>
              <label htmlFor="bootstrap-token">Bootstrap token</label>
              <input
                id="bootstrap-token"
                type="password"
                value={bootstrapToken}
                onChange={(event) => setBootstrapToken(event.target.value)}
                placeholder={t("部署时注入的一次性初始化凭证", "One-time bootstrap credential injected at deployment")}
                autoComplete="off"
                spellCheck={false}
              />
              <label htmlFor="display-name">{t("显示名称", "Display name")}</label>
              <input
                id="display-name"
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                placeholder={t("例如：平台管理员", "For example: Platform administrator")}
                autoComplete="name"
              />
            </>
          ) : null}
          <label htmlFor="username">{t("用户名", "Username")}</label>
          <input
            id="username"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            placeholder="admin"
            autoComplete="username"
            spellCheck={false}
            autoFocus={mode === "login"}
          />
          <label htmlFor="password">{t("密码", "Password")}</label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            placeholder={t("至少 12 个字符", "At least 12 characters")}
            autoComplete={mode === "setup" ? "new-password" : "current-password"}
          />
          {mode === "setup" ? (
            <>
              <label htmlFor="password-confirmation">{t("确认密码", "Confirm password")}</label>
              <input
                id="password-confirmation"
                type="password"
                value={passwordConfirmation}
                onChange={(event) => setPasswordConfirmation(event.target.value)}
                placeholder={t("再次输入密码", "Enter the password again")}
                autoComplete="new-password"
              />
            </>
          ) : null}
          {error ? <div className="form-error" role="alert">{error}</div> : null}
          <button className="primary-button" type="submit" disabled={submitting}>
            {submitting ? t("正在处理…", "Working…") : mode === "setup" ? t("创建管理员并进入", "Create administrator and continue") : t("登录", "Sign in")}
          </button>
        </form>
        <div className="connection-safety">
          <strong>{t("独立身份边界", "Independent identity boundary")}</strong>
          <span>{t("管理员账号属于当前 Control Center；不复用 IDS 用户，也不进入 Team 数据库。", "Administrator accounts belong to this Control Center. They do not reuse IDS users or enter Team databases.")}</span>
        </div>
      </section>
    </main>
  );
}

function AuthenticationLoading() {
  return <main className="auth-loading"><LoadingState /></main>;
}

function PasswordChangePage({
  session,
  onChanged,
  onLogout
}: {
  session: AuthSession;
  onChanged: (session: AuthSession) => void;
  onLogout: () => void;
}) {
  const { t } = useI18n();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function logout() {
    try {
      await logoutAdministrator(session.csrf_token);
    } finally {
      onLogout();
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (newPassword !== confirmation) return setError(t("两次输入的新密码不一致", "The new passwords do not match"));
    setSubmitting(true);
    setError("");
    try {
      const changedSession = await changeOwnPassword(session.csrf_token, currentPassword, newPassword);
      const confirmedSession = await resumeSessionWithRetry(undefined, sessionConfirmationAttempts);
      onChanged({ ...confirmedSession, user: changedSession.user });
    } catch (caught) {
      setError(authenticationError(caught, t));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="connection-page password-change-page">
      <section className="connection-intro">
        <div className="connection-language"><LanguageSwitcher inverse /></div>
        <PactureLogo className="brand-mark" />
        <p className="eyebrow">SECURITY REQUIRED</p>
        <h1>Change your<br />password</h1>
        <p className="connection-copy">{t("管理员为你设置的是临时密码。完成修改前，不能访问数据中心管理能力。", "An administrator assigned a temporary password. Change it before accessing data-center management.")}</p>
      </section>
      <section className="connection-card">
        <h2>{t("设置正式密码", "Set your permanent password")}</h2>
        <p>{t("当前账号", "Current account")}：{session.user.display_name}（{session.user.username}）</p>
        <form onSubmit={submit}>
          <label htmlFor="current-password">{t("当前临时密码", "Current temporary password")}</label>
          <input id="current-password" type="password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} autoComplete="current-password" />
          <label htmlFor="new-password">{t("新密码", "New password")}</label>
          <input id="new-password" type="password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} autoComplete="new-password" />
          <label htmlFor="new-password-confirmation">{t("确认新密码", "Confirm new password")}</label>
          <input id="new-password-confirmation" type="password" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} autoComplete="new-password" />
          {error ? <div className="form-error" role="alert">{error}</div> : null}
          <button className="primary-button" type="submit" disabled={submitting}>{submitting ? t("正在保存…", "Saving…") : t("保存新密码", "Save new password")}</button>
          <button className="text-button" type="button" onClick={logout}>{t("退出登录", "Sign out")}</button>
        </form>
      </section>
    </main>
  );
}

function DashboardProvider({
  session,
  setSession,
  onUnauthenticated,
  children
}: {
  session: AuthSession;
  setSession: (session: AuthSession) => void;
  onUnauthenticated: () => void;
  children: ReactNode;
}) {
  const { t } = useI18n();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);
  const [refreshRevision, setRefreshRevision] = useState(0);

  const refresh = useCallback(() => setRefreshRevision((revision) => revision + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    setRefreshing(data !== null);
    setError("");
    void (async () => {
      try {
        let nextData: DashboardData;
        try {
          nextData = await loadDashboard(controller.signal);
        } catch (caught) {
          if (caught instanceof DOMException && caught.name === "AbortError") return;
          if (!(caught instanceof APIError) || caught.status !== 401) throw caught;

          // A late response from an older request can be unauthorized even
          // though the browser already owns a newer valid session. Confirm the
          // session independently before discarding the authenticated UI.
          let recoveredSession: AuthSession;
          try {
            recoveredSession = await resumeSessionWithRetry(controller.signal, 3);
          } catch (sessionError) {
            if (sessionError instanceof APIError && sessionError.status === 401) {
              onUnauthenticated();
              return;
            }
            throw sessionError;
          }
          if (controller.signal.aborted) return;
          setSession(recoveredSession);
          nextData = await loadDashboard(controller.signal);
        }
        if (controller.signal.aborted) return;
        setData(nextData);
        setRefreshedAt(new Date());
      } catch (caught) {
        if (caught instanceof DOMException && caught.name === "AbortError") return;
        setError(caught instanceof Error ? caught.message : t("无法读取 Control Center 状态", "Unable to load Control Center status"));
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    })();
    return () => controller.abort();
  }, [refreshRevision, onUnauthenticated, setSession]);

  useEffect(() => {
    const timer = window.setInterval(refresh, refreshIntervalMilliseconds);
    return () => window.clearInterval(timer);
  }, [refresh]);

  const value = useMemo(
    () => ({ data, loading, refreshing, error, refreshedAt, refresh, session, setSession }),
    [data, loading, refreshing, error, refreshedAt, refresh, session, setSession]
  );
  return <DashboardContext.Provider value={value}>{children}</DashboardContext.Provider>;
}

function Shell({ onUnauthenticated }: { onUnauthenticated: () => void }) {
  const { t } = useI18n();
  const { data, loading, refreshing, error, refreshedAt, refresh, session } = useDashboard();
  const location = useLocation();
  const [centerLocations,setCenterLocations]=useState<Record<string,string>>({});
  useEffect(()=>{
    if(!["/monitoring","/alerts"].includes(location.pathname))return;
    if(location.pathname==="/monitoring"&&new URLSearchParams(location.search).get("section")==="alerts")return;
    const value=location.pathname+location.search;
    setCenterLocations(previous=>previous[location.pathname]===value?previous:{...previous,[location.pathname]:value});
  },[location.pathname,location.search]);
  // 新配置页只响应显式刷新，避免总览轮询覆盖正在填写的表单。
  const [managementRefreshKey, setManagementRefreshKey] = useState(0);
  const dataCenterID = data?.overview.data_center_id ?? "Data Center";
  const isReady = data?.overview.process.ready === true;
  const groups = navigationGroups(t);
  const activeNavigation = groups
    .flatMap((group) => group.items.map((item) => ({ ...item, group: group.label })))
    .find((item) => item.end ? location.pathname === item.to : location.pathname.startsWith(item.to));

  async function logout() {
    try {
      await logoutAdministrator(session.csrf_token);
    } finally {
      onUnauthenticated();
    }
  }

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="sidebar-brand">
          <PactureLogo className="sidebar-logo" />
          <div>
            <strong>{t("数据中心管理", "Data center control")}</strong>
            <span>Pacture Control Center</span>
          </div>
        </div>
        <div className="dc-identity">
          <span className={`status-dot ${isReady ? "status-dot-healthy" : "status-dot-neutral"}`} />
          <div>
            <strong>{dataCenterID}</strong>
            <span>{isReady ? t("Control Center 运行正常", "Control Center is healthy") : t("等待运行状态", "Waiting for runtime status")}</span>
          </div>
        </div>
        <nav className="primary-nav" aria-label={t("Control Center 主导航", "Control Center main navigation") }>
          {groups.map((group) => (
            <div className="nav-group" key={group.label}>
              <span className="nav-group-label">{group.label}</span>
              <div className="nav-group-items">
                {group.items.map((item) => <NavItem key={item.to} {...item} to={centerLocations[item.to]||item.to} />)}
              </div>
            </div>
          ))}
        </nav>
        <div className="sidebar-footer">
          <div className="administrator-avatar" aria-hidden="true">{session.user.display_name.slice(0, 1).toUpperCase()}</div>
          <div>
            <strong>{session.user.display_name}</strong>
            <span>@{session.user.username}</span>
          </div>
          <button type="button" onClick={logout} title={t("退出登录", "Sign out")}>{t("退出", "Exit")}</button>
        </div>
      </aside>
      <div className="workspace">
        <header className="topbar">
          <div>
            <span className="topbar-context">{activeNavigation?.group ?? t("数据中心", "Data center")}</span>
            <strong>{activeNavigation?.label ?? t("数据中心总览", "Data center overview")}</strong>
          </div>
          <div className="topbar-actions">
            <span className="refresh-time">
              {refreshedAt
                ? t(`更新于 ${formatTime(refreshedAt.toISOString())}`, `Updated at ${formatTime(refreshedAt.toISOString())}`)
                : t("等待首次同步", "Waiting for the first sync")}
            </span>
            <LanguageSwitcher />
            <button className="secondary-button" type="button" onClick={() => { refresh(); setManagementRefreshKey(value => value + 1); }} disabled={refreshing}>
              {refreshing ? t("刷新中…", "Refreshing…") : t("刷新", "Refresh")}
            </button>
          </div>
        </header>
        <main className="content">
          {error ? <div className="global-error" role="alert">{error}{t("，页面保留上一次成功数据。", ". The page is showing the last successful data.")}</div> : null}
          {loading && !data ? <LoadingState /> : (
            <Routes>
              <Route path="/" element={<OverviewPage />} />
              <Route path="/billing/*" element={<BillingPage csrfToken={session.csrf_token} administratorID={session.user.admin_user_id} />} />
              <Route path="/monitoring" element={<MonitoringPage session={session} refreshKey={managementRefreshKey} />} />
              <Route path="/alerts" element={<AlertsPage session={session} refreshKey={managementRefreshKey} />} />
              <Route path="/cluster" element={<ClusterPage />} />
			  <Route path="/ids" element={<IDSPage />} />
			  <Route path="/team-runtime" element={<TeamRuntimePage />} />
			  <Route path="/sandbox" element={<SandboxPage />} />
			  <Route path="/workspace-storage" element={<WorkspaceStoragePage />} />
              <Route path="/llm-gateway" element={<LLMGatewayPage />} />
              <Route path="/operations" element={<OperationsPage />} />
              <Route path="/access-keys" element={<AccessKeysRoute />} />
              <Route path="/administrators" element={<AdministratorsPage />} />
              <Route path="/global-users" element={<GlobalUsersPage session={session} refreshKey={managementRefreshKey} />} />
              <Route path="/identity-providers" element={<IdentityProvidersPage session={session} refreshKey={managementRefreshKey} />} />
              <Route path="/web-search" element={<WebSearchPage session={session} refreshKey={managementRefreshKey} />} />
              <Route path="/image-generation" element={<ImageGenerationPage session={session} refreshKey={managementRefreshKey} />} />
              <Route path="/email" element={<EmailManagementPage session={session} refreshKey={managementRefreshKey} />} />
              <Route path="/artifact-storage" element={<ArtifactStoragePage session={session} refreshKey={managementRefreshKey} />} />
              <Route path="/node-storage" element={<NodeStoragePage session={session} />} />
              <Route path="/applications" element={<ApplicationManagementPage session={session} refreshKey={managementRefreshKey} />} />
              <Route path="/release-center" element={<ReleaseCenterManagementPage session={session} refreshKey={managementRefreshKey} />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          )}
        </main>
      </div>
    </div>
  );
}

function SandboxPage() {
	const { t } = useI18n();
	const { session } = useDashboard();
 const [sandboxQuery] = useSearchParams();
 const selectedSandbox = sandboxQuery.get("sandbox_id") ?? "";
	const component = useComponent("sandbox");
	const [runtime, setRuntime] = useState<SandboxRuntimeSnapshot | null>(null);
	const [operations, setOperations] = useState<IDSOperation[]>([]);
	const [selectedTasks, setSelectedTasks] = useState<{ sandboxID: string; items: SandboxRuntimeTask[] } | null>(null);
	const [reason, setReason] = useState("");
	const [loading, setLoading] = useState(true);
	const [submittingSandboxID, setSubmittingSandboxID] = useState("");
	const [error, setError] = useState("");
	const [notice, setNotice] = useState("");
 const [retentionNotice, setRetentionNotice] = useState<RetentionNotice | null>(null);
 const [reclaimNotice, setReclaimNotice] = useState<ReclaimNotice | null>(null);
	const [section, setSection] = useState<"runtimes" | "policy" | "operations" | "databases">("runtimes");

	const refreshRuntime = useCallback(async () => {
		setLoading(true);
		setError("");
		try {
			const [nextRuntime, nextOperations] = await Promise.all([
				getSandboxRuntime(),
				listSandboxOperations()
			]);
			setRuntime(nextRuntime);
			setOperations(nextOperations);
		} catch (caught) {
			setError(authenticationError(caught, t));
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		void refreshRuntime();
	}, [refreshRuntime]);

	async function inspectTasks(sandboxID: string) {
		setError("");
		try {
			setSelectedTasks({ sandboxID, items: await listSandboxRuntimeTasks(sandboxID) });
		} catch (caught) {
			setError(authenticationError(caught, t));
		}
	}

	async function applyRuntimeAction(sandboxID: string, action: "reclaim" | "keep-alive" | "clear") {
		if (!reason.trim()) {
			setError(t("操作 Sandbox 运行实例前必须填写原因", "Enter a reason before operating on a Sandbox runtime"));
			return;
		}
		if (action === "reclaim" && !window.confirm(t(`确认回收运行实例 ${sandboxID}？正在执行的任务会由 Sandbox Control Plane 拒绝回收。`, `Reclaim runtime ${sandboxID}? Sandbox Control Plane will refuse while a task is running.`))) {
			return;
		}
		setSubmittingSandboxID(sandboxID);
		setError("");
		setNotice("");
 setReclaimNotice(null);
 setRetentionNotice(null);
		try {
			if (action === "reclaim") {
				const result = await reclaimSandboxRuntime(session.csrf_token, reason.trim(), sandboxID);
                setReclaimNotice({ sandboxID, state: result.operation.state === "succeeded" && !result.result?.deletion_confirmed ? "pending" : result.operation.state, errorCode: result.result?.error_code ?? "" });
			} else {
                const result = await keepAliveSandboxRuntime(session.csrf_token, reason.trim(), sandboxID, action === "keep-alive" ? 3600 : null);
                setRetentionNotice({sandboxID, state: result.operation.state, operationID: result.operation.operation_id || result.command_id || "", errorCode: result.result?.error_code ?? "", until: result.result?.keep_alive_until ?? null});
			}
			setReason("");
			await refreshRuntime();
		} catch (caught) {
			setError(authenticationError(caught, t));
		} finally {
			setSubmittingSandboxID("");
		}
	}

	const totals = runtime?.fleet.totals;
 const visibleRuntimes = runtime?.runtimes.filter(item => !selectedSandbox || item.sandbox_id === selectedSandbox) ?? [];
	return (
		<div className="page-stack">
			<PageHeader
				eyebrow="SANDBOX RUNTIME CONTROL"
				title={t("Sandbox 运行与开发数据库", "Sandbox runtimes and development databases")}
				description={t("管理 Sandbox 运行实例、资源回收和开发数据库接入；修改会记录管理员身份与操作原因。", "Manage Sandbox runtimes, resource reclamation, and development database access. Changes record administrator identity and the operation reason.")}
				status={component ? <StatusBadge status={component.status} /> : undefined}
			/>
			{error ? <div className="global-error administrator-message" role="alert">{error}</div> : null}
			{notice ? <div className="success-message" role="status">{notice}</div> : null}
            {reclaimNotice ? <RuntimeReclaimNotice result={reclaimNotice} /> : null}
            {retentionNotice ? <RuntimeRetentionNotice result={retentionNotice} /> : null}
			<PageTabs value={section} onChange={setSection} items={[
				{ value: "runtimes", label: t("运行实例", "Runtimes"), count: runtime?.runtimes.length ?? 0 },
				{ value: "databases", label: t("开发数据库", "Development databases") },
				{ value: "policy", label: t("生命周期策略", "Lifecycle policy") },
				{ value: "operations", label: t("人工操作", "Manual operations"), count: operations.length }
			]} />
			{section === "databases" ? <SandboxDevelopmentDatabases csrfToken={session.csrf_token} onSaved={() => void refreshRuntime()} /> : loading && !runtime ? <LoadingState /> : runtime ? <>
				<section className="metric-grid">
					<MetricCard label={t("活跃运行实例", "Active runtimes")} value={String(runtime.runtimes.length)} note={`${totals?.team_count ?? 0} Teams`} tone="blue" />
					<MetricCard label={t("分配 CPU", "Allocated CPU")} value={`${totals?.allocated_cpu_milli ?? 0}m`} note="active allocation" tone="neutral" />
					<MetricCard label={t("分配内存", "Allocated memory")} value={`${totals?.allocated_mem_mib ?? 0} MiB`} note="active allocation" tone="neutral" />
					<MetricCard label={t("回收策略", "Reclaim policy")} value={runtime.reclaim_policy.enabled ? t("已启用", "Enabled") : t("未启用", "Disabled")} note={runtime.reclaim_policy.configured ? "explicit policy" : "built-in defaults"} tone={runtime.reclaim_policy.enabled ? "green" : "orange"} />
				</section>

				{section === "runtimes" ? <>
				<section className="panel">
					<div className="panel-header"><div><span className="section-kicker">ACTIVE RUNTIMES</span><h2>{t("运行实例", "Runtimes")}</h2></div><button className="secondary-button" type="button" onClick={refreshRuntime} disabled={loading || !!submittingSandboxID}>{t("刷新", "Refresh")}</button></div>
					<label className="operation-reason">{t("操作原因", "Operation reason")}<input value={reason} onChange={(event) => setReason(event.target.value)} placeholder={t("回收或保留实例时必填并写入审计", "Required for reclaim or retention and recorded in the audit log")} /></label>
					{selectedSandbox ? <p>{t("当前定位沙箱", "Selected sandbox")}: <code>{selectedSandbox}</code> · <Link to="/sandbox">{t("查看全部", "View all")}</Link></p> : null}
                    {visibleRuntimes.length === 0 ? <div className="administrator-loading">{selectedSandbox ? t("当前运行清单中未找到此沙箱，不代表其历史用量为零。", "Sandbox not found in the current runtime inventory; this does not imply zero historical usage.") : t("当前没有活跃 Sandbox 运行实例。", "There are no active Sandbox runtimes.")}</div> : <div className="administrator-table-wrap"><table className="administrator-table"><thead><tr><th>Sandbox</th><th>Team / Workspace</th><th>{t("资源", "Resources")}</th><th>{t("空闲 / 生命周期", "Idle / lifecycle")}</th><th>{t("保留", "Retention")}</th><th>{t("操作", "Actions")}</th></tr></thead><tbody>{visibleRuntimes.map((item) => <tr key={item.sandbox_id}><td><strong>{item.sandbox_id}</strong>{item.status === "reclaiming" ? <span>{t("回收中 / 待核对", "Reclaiming / awaiting reconciliation")}</span> : null}<span>{item.namespace} · {item.pod_name}</span></td><td><strong>{item.team_id || "—"}</strong><span>{item.workspace_id}{item.project_ref ? ` · ${item.project_ref}` : ""}</span></td><td><strong>{item.resource_class || "default"}</strong><span>{item.scope}</span></td><td><strong>{formatDuration(item.idle_seconds)} idle</strong><span>{formatDuration(item.age_seconds)} old</span></td><td><span className={`user-state user-state-${item.pinned ? "active" : "disabled"}`}>{item.pinned ? "pinned" : "normal"}</span>{item.keep_alive_until ? <small>until {formatDateTime(item.keep_alive_until)}</small> : null}</td><td><div className="table-actions"><button className="secondary-button" type="button" onClick={() => void inspectTasks(item.sandbox_id)}>Tasks</button><button className="secondary-button" type="button" disabled={!!submittingSandboxID || item.status === "reclaiming" || (retentionNotice?.sandboxID === item.sandbox_id && retentionNotice.state === "pending")} onClick={() => void applyRuntimeAction(item.sandbox_id, item.pinned ? "clear" : "keep-alive")}>{item.pinned ? t("取消保留", "Clear retention") : t("保留 1h", "Retain 1h")}</button><button className="danger-button" type="button" disabled={!!submittingSandboxID || item.status === "reclaiming" || (retentionNotice?.sandboxID === item.sandbox_id && retentionNotice.state === "pending")} onClick={() => void applyRuntimeAction(item.sandbox_id, "reclaim")}>{submittingSandboxID === item.sandbox_id ? t("执行中…", "Working…") : t("回收", "Reclaim")}</button></div></td></tr>)}</tbody></table></div>}
				</section>

				{selectedTasks ? <section className="panel"><div className="panel-header"><div><span className="section-kicker">RUNTIME TASKS</span><h2>{selectedTasks.sandboxID} {t("最近任务", "recent tasks")}</h2></div><button className="secondary-button" type="button" onClick={() => setSelectedTasks(null)}>{t("关闭", "Close")}</button></div>{selectedTasks.items.length === 0 ? <div className="administrator-loading">{t("没有关联任务。", "No related tasks.")}</div> : <div className="administrator-table-wrap"><table className="administrator-table"><thead><tr><th>Task</th><th>{t("类型", "Type")}</th><th>{t("状态", "Status")}</th><th>{t("更新时间", "Updated")}</th></tr></thead><tbody>{selectedTasks.items.map((item) => <tr key={item.task_id}><td><strong>{item.task_id}</strong><span>{item.role}</span></td><td>{item.task_type}</td><td>{item.status}</td><td>{formatDateTime(item.updated_at)}</td></tr>)}</tbody></table></div>}</section> : null}
				</> : null}

				{section === "policy" ? <section className="panel"><div className="panel-header"><div><span className="section-kicker">RECLAIM POLICY</span><h2>{t("生命周期策略与最近回收", "Lifecycle policy and recent reclamation")}</h2></div><span className="panel-meta">{t("观测于", "Observed at")} {formatTime(runtime.observed_at)}</span></div><div className="runtime-policy-summary"><span>Conversation idle <strong>{formatDuration(runtime.reclaim_policy.conversation_idle_ttl_seconds)}</strong></span><span>Project idle <strong>{formatDuration(runtime.reclaim_policy.project_idle_ttl_seconds)}</strong></span><span>Max lifetime <strong>{formatDuration(runtime.reclaim_policy.max_lifetime_seconds)}</strong></span><span>Metrics <strong>{runtime.metrics.available ? "available" : "unavailable"}</strong></span></div>{runtime.reclaim_log.length === 0 ? <div className="administrator-loading">{t("尚无回收记录。", "No reclamation records yet.")}</div> : <div className="administrator-table-wrap"><table className="administrator-table"><thead><tr><th>Sandbox</th><th>Actor</th><th>{t("原因", "Reason")}</th><th>{t("时间", "Time")}</th></tr></thead><tbody>{runtime.reclaim_log.map((item, index) => <tr key={`${item.sandbox_id}-${item.at}-${index}`}><td><strong>{item.sandbox_id}</strong><span>{item.workspace_id} · {item.pod_name}</span></td><td>{item.actor}</td><td>{item.reason}</td><td>{formatDateTime(item.at)}</td></tr>)}</tbody></table></div>}</section> : null}

				{section === "operations" ? <section className="panel"><div className="panel-header"><div><span className="section-kicker">CONTROL CENTER AUDIT</span><h2>{t("最近人工操作", "Recent manual operations")}</h2></div><span className="panel-meta">{t("保存在 Control Center TiDB", "Stored in Control Center TiDB")}</span></div>{operations.length === 0 ? <div className="administrator-loading">{t("尚无 Sandbox 人工操作。", "No manual Sandbox operations yet.")}</div> : <div className="administrator-table-wrap"><table className="administrator-table"><thead><tr><th>Sandbox</th><th>{t("操作", "Action")}</th><th>{t("原因", "Reason")}</th><th>{t("结果", "Result")}</th><th>{t("时间", "Time")}</th></tr></thead><tbody>{operations.map((operation) => <tr key={operation.operation_id}><td>{operation.target_id}</td><td>{({ "development_database.update": t("保存开发数据库配置", "Save development database configuration"), "development_database.check": t("检查开发数据库连接", "Check development database connection"), "development_database.release": t("释放开发数据库", "Release development database") } as Record<string, string>)[operation.action] ?? operation.action}</td><td>{operation.reason}</td><td><span className={`user-state user-state-${operation.state === "succeeded" ? "active" : "disabled"}`}>{operation.state === "succeeded" ? t("成功", "Succeeded") : operation.state === "failed" ? t("失败", "Failed") : t("处理中", "Pending")}</span>{operation.message ? <small>{<DiagnosticText value={operation}/>}</small> : null}</td><td>{formatDateTime(operation.created_at)}</td></tr>)}</tbody></table></div>}</section> : null}
			</> : <MissingComponent title={t("无法读取 Sandbox Runtime", "Unable to load Sandbox Runtime")} />}
		</div>
	);
}

interface WorkspaceStorageForm {
	enabled: boolean;
	backend: WorkspaceStorageBackend;
	server: string;
	exportPath: string;
	rootPath: string;
	region: string;
	eksCluster: string;
	environment: string;
	efsNamePrefix: string;
	subnetIDs: string;
	sourceSecurityGroupIDs: string;
	efsSecurityGroupID: string;
	accountID: string;
	posixUID: string;
	posixGID: string;
	directoryMode: string;
	initTimeoutSeconds: string;
}

function workspaceStorageForm(configuration: WorkspaceStorageConfiguration): WorkspaceStorageForm {
	return {
		enabled: configuration.enabled,
		backend: configuration.backend === "local_nfs" ? "local_nfs" : "aws_efs",
		server: configuration.local.server ?? "",
		exportPath: configuration.local.export_path ?? "",
		rootPath: configuration.local.root_path ?? "",
		region: configuration.aws.region ?? "",
		eksCluster: configuration.aws.eks_cluster ?? "",
		environment: configuration.aws.environment ?? "",
		efsNamePrefix: configuration.aws.efs_name_prefix ?? "",
		subnetIDs: (configuration.aws.subnet_ids ?? []).join(", "),
		sourceSecurityGroupIDs: (configuration.aws.source_security_group_ids ?? []).join(", "),
		efsSecurityGroupID: configuration.aws.efs_security_group_id ?? "",
		accountID: configuration.aws.account_id ?? "",
		posixUID: String(configuration.aws.posix_uid),
		posixGID: String(configuration.aws.posix_gid),
		directoryMode: configuration.directory_mode,
		initTimeoutSeconds: String(configuration.init_timeout_seconds)
	};
}

function commaSeparatedValues(value: string): string[] {
	return value.split(",").map((item) => item.trim()).filter(Boolean);
}

function WorkspaceStoragePage() {
	const { t } = useI18n();
	const { session } = useDashboard();
	const component = useComponent("sandbox");
	const [snapshot, setSnapshot] = useState<WorkspaceStorageSnapshot | null>(null);
	const [form, setForm] = useState<WorkspaceStorageForm | null>(null);
	const [operations, setOperations] = useState<IDSOperation[]>([]);
	const [reason, setReason] = useState("");
	const [loading, setLoading] = useState(true);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState("");
	const [notice, setNotice] = useState("");
	const [section, setSection] = useState<"configuration" | "systems" | "operations">("configuration");

	const refreshStorage = useCallback(async () => {
		setLoading(true);
		setError("");
		try {
			const [nextSnapshot, nextOperations] = await Promise.all([
				getWorkspaceStorage(),
				listSandboxOperations()
			]);
			setSnapshot(nextSnapshot);
			setForm(workspaceStorageForm(nextSnapshot.configuration));
			setOperations(nextOperations.filter((item) => item.action.startsWith("workspace_storage.")));
		} catch (caught) {
			setError(authenticationError(caught, t));
		} finally {
			setLoading(false);
		}
	}, [t]);

	useEffect(() => {
		void refreshStorage();
	}, [refreshStorage]);

	async function saveConfiguration(event: FormEvent) {
		event.preventDefault();
		if (!form || !reason.trim()) {
			setError(t("保存工作区存储前必须填写变更原因", "Enter a change reason before saving workspace storage"));
			return;
		}
		const posixUID = Number(form.posixUID);
		const posixGID = Number(form.posixGID);
		const initTimeoutSeconds = Number(form.initTimeoutSeconds);
		if (![posixUID, posixGID, initTimeoutSeconds].every(Number.isInteger)) {
			setError(t("POSIX UID、GID 和初始化超时必须为整数", "POSIX UID, GID, and initialization timeout must be integers"));
			return;
		}
		setSubmitting(true);
		setError("");
		setNotice("");
		try {
			await updateWorkspaceStorageConfiguration(session.csrf_token, reason.trim(), {
				enabled: form.enabled,
				backend: form.backend,
				local: { server: form.server.trim(), export_path: form.exportPath.trim(), root_path: form.rootPath.trim() },
				aws: {
					region: form.region.trim(),
					eks_cluster: form.eksCluster.trim(),
					environment: form.environment.trim(),
					efs_name_prefix: form.efsNamePrefix.trim(),
					subnet_ids: commaSeparatedValues(form.subnetIDs),
					source_security_group_ids: commaSeparatedValues(form.sourceSecurityGroupIDs),
					efs_security_group_id: form.efsSecurityGroupID.trim(),
					account_id: form.accountID.trim(),
					posix_uid: posixUID,
					posix_gid: posixGID
				},
				directory_mode: form.directoryMode.trim(),
				init_timeout_seconds: initTimeoutSeconds
			});
			setReason("");
			setNotice(t("工作区存储配置已由 Sandbox 应用并写入审计。", "Workspace storage configuration was applied by Sandbox and recorded in the audit log."));
			await refreshStorage();
		} catch (caught) {
			setError(authenticationError(caught, t));
		} finally {
			setSubmitting(false);
		}
	}

	const teamCount = snapshot?.systems.teams.length ?? 0;
	const projectCount = snapshot?.systems.teams.reduce((total, item) => total + item.projects.length, 0) ?? 0;
	const provider = snapshot?.configuration.backend === "aws_efs" ? "AWS EFS" : snapshot?.configuration.backend === "local_nfs" ? "Local NFS" : "—";

	return <div className="page-stack">
		<PageHeader
			eyebrow="SANDBOX WORKSPACE STORAGE"
			title={t("工作区存储", "Workspace Storage")}
			description={t("Control Center 管理 provider 与存储系统；实际配置由 Sandbox 持久化和执行。AWS 凭据来自运行时 IAM/IRSA，不进入浏览器或 Control Center 数据库。", "Control Center manages providers and storage systems while Sandbox persists and applies the configuration. AWS credentials come from runtime IAM/IRSA and never enter the browser or Control Center database.")}
			status={component ? <StatusBadge status={component.status} /> : undefined}
		/>
		{error ? <div className="global-error administrator-message" role="alert">{error}</div> : null}
		{notice ? <div className="success-message" role="status">{notice}</div> : null}
		{loading && !snapshot ? <LoadingState /> : snapshot && form ? <>
			<section className="metric-grid">
				<MetricCard label="Provider" value={provider} note={snapshot.configuration.configured ? t("已持久化", "Persisted") : t("尚未保存", "Not saved")} tone="blue" />
				<MetricCard label={t("运行状态", "Runtime state")} value={snapshot.configuration.enabled ? t("启用", "Enabled") : t("停用", "Disabled")} note={snapshot.configuration.aws.credential_source ?? t("由 Sandbox 执行", "Applied by Sandbox")} tone={snapshot.configuration.enabled ? "green" : "neutral"} />
				<MetricCard label="Team storage" value={String(teamCount)} note={`${projectCount} project mounts`} tone="neutral" />
				<MetricCard label={t("最近观测", "Last observed")} value={formatTime(snapshot.observed_at)} note={snapshot.configuration.updated_by ?? t("无更新人记录", "No updater recorded")} tone="neutral" />
			</section>
			<PageTabs value={section} onChange={setSection} items={[
				{ value: "configuration", label: t("Provider 配置", "Provider configuration") },
				{ value: "systems", label: t("存储系统", "Storage systems"), count: teamCount },
				{ value: "operations", label: t("人工操作", "Manual operations"), count: operations.length }
			]} />
			{section === "configuration" ? <form className="panel configuration-form" onSubmit={saveConfiguration}>
				<div className="panel-header"><div><span className="section-kicker">PROVIDER CONFIGURATION</span><h2>{t("工作区存储后端", "Workspace storage backend")}</h2></div><span className="read-only-label">Sandbox owned</span></div>
				<div className="configuration-form-body configuration-form-two-column">
					<label className="checkbox-label"><input type="checkbox" checked={form.enabled} onChange={(event) => setForm({ ...form, enabled: event.target.checked })} />{t("启用持久化工作区存储", "Enable persistent workspace storage")}</label>
					<label>Provider<select value={form.backend} onChange={(event) => setForm({ ...form, backend: event.target.value as WorkspaceStorageBackend })}><option value="aws_efs">AWS EFS</option><option value="local_nfs">Local NFS</option></select></label>
					{form.backend === "aws_efs" ? <>
						<label>Region<input value={form.region} onChange={(event) => setForm({ ...form, region: event.target.value })} /></label>
						<label>EKS cluster<input value={form.eksCluster} onChange={(event) => setForm({ ...form, eksCluster: event.target.value })} /></label>
						<label>Environment<input value={form.environment} onChange={(event) => setForm({ ...form, environment: event.target.value })} /></label>
						<label>EFS name prefix<input value={form.efsNamePrefix} onChange={(event) => setForm({ ...form, efsNamePrefix: event.target.value })} /></label>
						<label>Subnet IDs<input value={form.subnetIDs} onChange={(event) => setForm({ ...form, subnetIDs: event.target.value })} placeholder={t("逗号分隔", "Comma-separated")} /></label>
						<label>Source security group IDs<input value={form.sourceSecurityGroupIDs} onChange={(event) => setForm({ ...form, sourceSecurityGroupIDs: event.target.value })} placeholder={t("逗号分隔", "Comma-separated")} /></label>
						<label>EFS security group ID<input value={form.efsSecurityGroupID} onChange={(event) => setForm({ ...form, efsSecurityGroupID: event.target.value })} /></label>
						<label>AWS account ID<input value={form.accountID} onChange={(event) => setForm({ ...form, accountID: event.target.value })} /></label>
						<label>POSIX UID<input type="number" min="0" value={form.posixUID} onChange={(event) => setForm({ ...form, posixUID: event.target.value })} /></label>
						<label>POSIX GID<input type="number" min="0" value={form.posixGID} onChange={(event) => setForm({ ...form, posixGID: event.target.value })} /></label>
					</> : <>
						<label>NFS server<input value={form.server} onChange={(event) => setForm({ ...form, server: event.target.value })} /></label>
						<label>Export path<input value={form.exportPath} onChange={(event) => setForm({ ...form, exportPath: event.target.value })} /></label>
						<label>Root path<input value={form.rootPath} onChange={(event) => setForm({ ...form, rootPath: event.target.value })} /></label>
					</>}
					<label>{t("目录权限", "Directory mode")}<input value={form.directoryMode} onChange={(event) => setForm({ ...form, directoryMode: event.target.value })} /></label>
					<label>{t("初始化超时（秒）", "Initialization timeout (seconds)")}<input type="number" min="1" max="3600" value={form.initTimeoutSeconds} onChange={(event) => setForm({ ...form, initTimeoutSeconds: event.target.value })} /></label>
					<label className="configuration-submit-wide">{t("变更原因", "Change reason")}<input value={reason} onChange={(event) => setReason(event.target.value)} placeholder={t("必填并写入 Control Center 操作审计", "Required and recorded in the Control Center operation audit")} /></label>
					<button className="primary-button configuration-submit-wide" type="submit" disabled={submitting}>{submitting ? t("保存中…", "Saving…") : t("保存工作区存储", "Save workspace storage")}</button>
				</div>
			</form> : null}
			{section === "systems" ? <section className="panel"><div className="panel-header"><div><span className="section-kicker">BOUND STORAGE SYSTEMS</span><h2>{t("Team 与项目挂载", "Team and project mounts")}</h2></div><button className="secondary-button" type="button" onClick={refreshStorage} disabled={loading}>{t("刷新", "Refresh")}</button></div>{teamCount === 0 ? <div className="administrator-loading">{t("当前没有已创建的 Team 存储系统。", "There are no provisioned Team storage systems.")}</div> : <div className="administrator-table-wrap"><table className="administrator-table"><thead><tr><th>Team</th><th>Provider</th><th>{t("状态", "Status")}</th><th>{t("存储引用", "Storage reference")}</th><th>{t("项目挂载", "Project mounts")}</th></tr></thead><tbody>{snapshot.systems.teams.map((item) => <tr key={item.team_id}><td><strong>{item.team_id}</strong><span>{item.team_root_path}</span></td><td>{item.backend}</td><td><span className={`user-state user-state-${item.status === "active" ? "active" : "disabled"}`}>{item.status}</span></td><td><strong>{item.storage_ref}</strong><span>{item.server}{item.export_path}</span></td><td>{item.projects.length}</td></tr>)}</tbody></table></div>}</section> : null}
			{section === "operations" ? <section className="panel"><div className="panel-header"><div><span className="section-kicker">AUDITED OPERATIONS</span><h2>{t("最近存储配置操作", "Recent storage configuration operations")}</h2></div></div>{operations.length === 0 ? <div className="administrator-loading">{t("尚无工作区存储操作。", "No workspace storage operations yet.")}</div> : <div className="administrator-table-wrap"><table className="administrator-table"><thead><tr><th>{t("操作", "Action")}</th><th>{t("原因", "Reason")}</th><th>{t("结果", "Result")}</th><th>{t("时间", "Time")}</th></tr></thead><tbody>{operations.map((operation) => <tr key={operation.operation_id}><td>{operation.action}</td><td>{operation.reason}</td><td><span className={`user-state user-state-${operation.state === "succeeded" ? "active" : "disabled"}`}>{operation.state}</span></td><td>{formatDateTime(operation.created_at)}</td></tr>)}</tbody></table></div>}</section> : null}
		</> : <MissingComponent title={t("无法读取工作区存储", "Unable to load workspace storage")} />}
	</div>;
}

function TeamRuntimePage() {
 const runtimeLocation = useLocation();
 const selectedTeam = new URLSearchParams(runtimeLocation.search).get("team_id") ?? "";
	const { t } = useI18n();
	const { session } = useDashboard();
	const component = useComponent("team-placement");
	const [runtime, setRuntime] = useState<TeamRuntimeSnapshot | null>(null);
	const [capacityInstance, setCapacityInstance] = useState<TeamRuntimeSnapshot["instances"][number] | null>(null);
	const [fleetAction,setFleetAction]=useState<FleetAction|null>(null);
	const [teams, setTeams] = useState<IDSTeam[]>([]);
	// 放置表的 Owner 列原本显示的是"持有租约的 API 实例"，与"谁负责这个团队"是两件事。
	// 这里按团队的 owner_user_id 去 IDS 全局用户查姓名与邮箱；解析不到就显示账号 ID，
	// 绝不回落到实例名，避免把机器当成负责人。
	const [owners, setOwners] = useState<Record<string, { name: string; email: string } | "unknown">>({});
	const [operations, setOperations] = useState<IDSOperation[]>([]);
	const [reason, setReason] = useState("");
	const [loading, setLoading] = useState(true);
	const [submittingTeamID, setSubmittingTeamID] = useState("");
	const [error, setError] = useState("");
	const [notice, setNotice] = useState("");
	const [section, setSection] = useState<"placements" | "instances" | "operations" | "configuration">("placements");

	const refreshRuntime = useCallback(async () => {
		setLoading(true);
		setError("");
		try {
			const [nextRuntime, nextOperations] = await Promise.all([
				getTeamRuntime(),
				listTeamRuntimeOperations()
			]);
			setRuntime(nextRuntime);
			setOperations(nextOperations);
			try {
				const nextTeams = await listIDSTeams({
					page: 1,
					page_size: 100,
					data_center_id: nextRuntime.data_center_id,
					sort: "team_id",
					direction: "asc"
				});
				setTeams(nextTeams.items);
			} catch {
				// IDS is supplementary naming data; its outage must not hide the local runtime inventory.
				setTeams([]);
			}
		} catch (caught) {
			setError(authenticationError(caught, t));
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		void refreshRuntime();
	}, [refreshRuntime]);

	// 只解析当前放置表里出现的负责人；按 user id 去重，失败标记为 unknown。
	// IDS 的用户检索同时匹配 user_id / 邮箱 / 姓名，所以必须按 user_id 精确取回，
	// 否则可能把同名的另一个人显示成负责人。
	useEffect(() => {
		if (!runtime?.placements.length || teams.length === 0) return;
		const byTeam = new Map(teams.map((team) => [team.team_id, team.owner_user_id]));
		const wanted = new Set<string>();
		for (const item of runtime.placements) {
			const ownerId = byTeam.get(item.placement.team_id);
			if (ownerId) wanted.add(ownerId);
		}
		const pending = [...wanted].filter((id) => owners[id] === undefined).slice(0, 50);
		if (pending.length === 0) return;
		let live = true;
		void Promise.allSettled(pending.map((id) => listGlobalUsers(id, 0))).then((results) => {
			if (!live) return;
			setOwners((current) => {
				const next = { ...current };
				results.forEach((result, index) => {
					const id = pending[index];
					if (result.status !== "fulfilled") {
						next[id] = "unknown";
						return;
					}
					const match = result.value.items.find((user) => user.user_id === id);
					next[id] = match
						? { name: match.display_name || match.email, email: match.email }
						: "unknown";
				});
				return next;
			});
		});
		return () => { live = false; };
	}, [runtime, teams, owners]);

	useEffect(() => {
		if (!runtime?.active_upgrade && !runtime?.moves?.length && !runtime?.instances.some(i => i.capacity_policy && i.capacity_policy.revision !== i.capacity_policy.applied_revision) && !operations.some(o=>o.state==="pending")) return;
		const timer = window.setInterval(() => void refreshRuntime(), 10_000);
		return () => window.clearInterval(timer);
	}, [runtime, operations, refreshRuntime]);

	async function reconcile(item: TeamRuntimeSnapshot["placements"][number]) {
		if (runtime?.active_upgrade) return;
		if (!reason.trim()) {
			setError(t("执行 Team reconcile 前必须填写操作原因", "Enter an operation reason before reconciling the Team"));
			return;
		}
		const placement = item.placement;
		setSubmittingTeamID(placement.team_id);
		setError("");
		setNotice("");
		try {
			const result = await reconcileTeamRuntime(
				session.csrf_token,
				reason.trim(),
				placement.team_id,
				placement.record_revision
			);
			setNotice(t(
				`Team ${result.team_id} 已完成 ${result.action}，owner 为 ${result.owner_instance_id}。`,
				`Team ${result.team_id} completed ${result.action}; owner is ${result.owner_instance_id}.`
			));
			setReason("");
			await refreshRuntime();
		} catch (caught) {
			setError(authenticationError(caught, t));
		} finally {
			setSubmittingTeamID("");
		}
	}

	const readyInstances = runtime?.instances.filter((item) => item.lifecycle === "ready").length ?? 0;
	const readyRoutes = runtime?.placements.filter((item) => item.route_eligible).length ?? 0;
	const loadedTeams = runtime?.instances.reduce((sum, item) => sum + item.loaded_team_count, 0) ?? 0;
	const totalSlots = runtime?.instances.reduce((sum, item) => sum + item.max_team_slots, 0) ?? 0;
	const teamNames = new Map(teams.map((team) => [team.team_id, team.display_name]));
	const ownerIdsByTeam = new Map(teams.map((team) => [team.team_id, team.owner_user_id]));

	return (
		<div className="page-stack team-runtime-page">
			<PageHeader
				eyebrow="TEAM RUNTIME CONTROL"
				title={t("Pacture API 与 Team Placement", "Pacture API and Team Placement")}
				description={t("聚合 Pacture API 容量、Team 运行归属、租约和路由状态，并记录运行协调与维护升级操作。", "Observe Pacture API capacity, Team ownership, leases, and routes, with audited reconciliation and maintenance upgrades.")}
				status={component ? <StatusBadge status={component.status} /> : undefined}
			/>
			{error && section !== "configuration" ? <div className="global-error administrator-message" role="alert">{error}</div> : null}
			{notice ? <div className="success-message" role="status">{notice}</div> : null}
			{runtime?.active_upgrade ? <MaintenanceRecovery operation={runtime.active_upgrade} operations={operations} csrfToken={session.csrf_token} onRefresh={refreshRuntime} onOperations={() => setSection("operations")} /> : null}
			{runtime?.moves?.length ? <TeamMoveProgress moves={runtime.moves}/> : null}
					<PageTabs
						value={section}
						onChange={setSection}
						items={[
							{ value: "placements", label: t("Team 运行归属", "Team placements"), count: runtime?.placements.length },
							{ value: "instances", label: t("Pacture API 实例", "Pacture API instances"), count: runtime?.instances.length },
							{ value: "configuration", label: t("升级配置", "Upgrade configuration") },
							{ value: "operations", label: t("执行与审计", "Execution and audit"), count: operations.length }
						]}
					/>
			{section === "configuration" ? <UpgradeConfiguration csrfToken={session.csrf_token} onOperations={() => { setSection("operations"); void refreshRuntime(); }} /> : null}
			{section === "configuration" ? null : loading && !runtime ? <LoadingState /> : runtime ? (
				<>
					<section className="metric-grid">
						<MetricCard label="Pacture API" value={`${readyInstances} / ${runtime.instances.length}`} note="ready / registered" tone={readyInstances === runtime.instances.length ? "green" : "orange"} />
						<MetricCard label="Team Slot" value={`${loadedTeams} / ${totalSlots}`} note="loaded / capacity" tone="blue" />
						<MetricCard label={t("可路由 Team", "Routable Teams")} value={`${readyRoutes} / ${runtime.placements.length}`} note="lease + heartbeat + owner" tone={readyRoutes === runtime.placements.length ? "green" : "orange"} />
						<MetricCard label="IDS Team" value={String(teams.length)} note={`home in ${runtime.data_center_id}`} tone="neutral" />
					</section>


					{section === "instances" ? <section className="panel runtime-instances">
						<div className="panel-header"><div><span className="section-kicker">PACTURE API INVENTORY</span><h2>{t("实例与容量", "Instances and capacity")}</h2></div><button className="secondary-button" type="button" onClick={refreshRuntime} disabled={loading || !!submittingTeamID}>{t("刷新", "Refresh")}</button></div>
						<div className="administrator-table-wrap"><table className="administrator-table"><thead><tr><th>{t("实例", "Instance")}</th><th>{t("版本", "Version")}</th><th>Team slot</th><th>DB connections</th><th>{t("心跳", "Heartbeat")}</th><th>{t("操作", "Actions")}</th></tr></thead><tbody>{runtime.instances.map((instance) => <tr key={instance.instance_id}><td><strong>{instance.instance_id}</strong><span>{instance.lifecycle} · {instance.endpoint}</span><small>{instance.admission_policy?.accepting===false?t("已停止接纳新团队","Admission stopped"):t("可接纳新团队","Accepting new Teams")}</small></td><td><strong>{instance.release_version}</strong><span>schema {instance.supported_schema_version}</span></td><td>{instance.loaded_team_count} / {instance.max_team_slots}{instance.capacity_policy ? <small>{instance.capacity_policy.applied_revision === instance.capacity_policy.revision ? t("策略已生效", "Policy applied") : t(`目标 ${instance.capacity_policy.max_team_slots} · ${instance.capacity_policy.last_error ? "应用未成功，将自动重试" : "等待生效"}`, `Target ${instance.capacity_policy.max_team_slots} · ${instance.capacity_policy.last_error ? "application pending; retrying" : "pending"}`)} · r{instance.capacity_policy.revision}</small> : null}</td><td>{instance.db_connections_used} / {instance.db_connection_budget}</td><td>{formatDateTime(instance.heartbeat_at)}</td><td><button type="button" className="secondary-button" disabled={!!runtime.active_upgrade || instance.lifecycle !== "ready"} onClick={() => setCapacityInstance(instance)}>{t("调整容量", "Adjust capacity")}</button><button type="button" className="secondary-button" disabled={!!runtime.active_upgrade||operations.some(o=>o.target_id===instance.instance_id&&o.action==="instance.admission.apply"&&o.state==="pending")} onClick={()=>setFleetAction({kind:"admission",instance})}>{instance.admission_policy?.accepting===false?t("恢复接纳","Resume admission"):t("停止接纳","Stop admission")}</button></td></tr>)}</tbody></table></div>
					</section> : null}

					{section === "placements" ? <section className="panel">
              {selectedTeam ? <p>{t("当前团队", "Selected Team")}: {selectedTeam} · <Link to="/team-runtime">{t("查看全部团队", "View all Teams")}</Link>{!runtime.placements.some(item => item.placement.team_id === selectedTeam) ? <span> · {t("当前快照中未找到运行归属；不代表注册记录不存在。", "No placement in the current snapshot; this does not mean the registration is absent.")}</span> : null}</p> : null}
						<div className="panel-header"><div><span className="section-kicker">TEAM PLACEMENTS</span><h2>{t("Team 运行归属", "Team placements")}</h2></div><span className="panel-meta">{t("观测于", "Observed at")} {formatTime(runtime.observed_at)}</span></div>
						<label className="operation-reason">{t("操作原因", "Operation reason")}<input value={reason} onChange={(event) => setReason(event.target.value)} placeholder={t("执行 reconcile 时必填并写入审计", "Required for reconciliation and recorded in the audit log")} /></label>
						<div className="administrator-table-wrap"><table className="administrator-table"><thead><tr><th>{t("团队", "Team")}</th><th>{t("团队负责人", "Team owner")}</th><th>{t("路由与租约", "Routing & lease")}</th><th>{t("版本与数据库", "Version and database")}</th><th>{t("操作", "Actions")}</th></tr></thead><tbody>{runtime.placements.filter(item => !selectedTeam || item.placement.team_id === selectedTeam).map((item) => { const placement = item.placement; const ownerId = ownerIdsByTeam.get(placement.team_id) ?? ""; const owner = ownerId ? owners[ownerId] : undefined; return <tr key={placement.team_id}><td><strong>{teamNames.get(placement.team_id) ?? placement.team_id}</strong><span>{placement.team_id} · epoch {placement.placement_epoch}</span></td><td>{owner && owner !== "unknown" ? <><strong>{owner.name}</strong><span>{owner.email}</span></> : <><strong>{ownerId || t("未登记负责人", "No owner recorded")}</strong><span>{ownerId ? t("未能解析姓名，显示账号 ID", "Name unavailable; showing account ID") : t("该团队没有负责人记录", "This Team has no owner on record")}</span></>}</td><td><span className={`user-state user-state-${item.route_eligible ? "active" : "disabled"}`}>{item.route_eligible ? t("就绪", "ready") : t("需关注", "attention")}</span><small>{t("租约", "lease")} {placement.owner_instance_id} · {formatDateTime(placement.owner_lease_until)}</small><small>route rev {placement.route_revision} · record rev {placement.record_revision}</small></td><td><strong>API {placement.pacture_api_release} · Web {placement.web_release}</strong><span>schema {placement.schema_version} · {placement.database_binding_ref}</span></td><td>{runtime.moves?.some(m=>m.team_id===placement.team_id)?<span>{t("迁移中","Moving")}</span>:item.operation_lease ? <><span className="user-state user-state-disabled">{t("操作占用中", "operation busy")}</span><small>until {formatDateTime(item.operation_lease.lease_until)}</small></> : <button className="secondary-button" type="button" disabled={!!submittingTeamID || !!runtime.active_upgrade} onClick={() => void reconcile(item)}>{submittingTeamID === placement.team_id ? t("执行中…", "Working…") : t("重新协调", "Reconcile")}</button>}{!item.operation_lease&&!runtime.moves?.some(m=>m.team_id===placement.team_id)?<button type="button" className="secondary-button" disabled={!!runtime.active_upgrade} onClick={()=>setFleetAction({kind:"move",source:placement})}>{t("迁移团队","Move Team")}</button>:null}</td></tr>; })}</tbody></table></div>
					</section> : null}

					{section === "operations" ? <section className="panel">
						<div className="panel-header"><div><span className="section-kicker">AUDITED OPERATIONS</span><h2>{t("最近 Team Runtime 操作", "Recent Team Runtime operations")}</h2></div><span className="panel-meta">{t("保存在 Control Center TiDB", "Stored in Control Center TiDB")}</span></div>
						{operations.length === 0 ? <div className="administrator-loading">{t("尚无 Team Runtime 操作。", "No Team Runtime operations yet.")}</div> : <div className="administrator-table-wrap"><table className="administrator-table"><thead><tr><th>{t("对象", "Target")}</th><th>{t("操作", "Action")}</th><th>{t("原因", "Reason")}</th><th>{t("结果", "Result")}</th><th>{t("时间", "Time")}</th></tr></thead><tbody>{operations.map((operation) => <tr key={operation.operation_id}><td>{operation.target_id}</td><td>{operation.action}</td><td>{operation.reason}</td><td><RuntimeOperationResult operation={operation}/></td><td>{formatDateTime(operation.created_at)}</td></tr>)}</tbody></table></div>}
					</section> : null}
				</>
			) : <MissingComponent title={t("无法读取 Team Runtime", "Unable to load Team Runtime")} />}
			{capacityInstance ? <CapacityDialog instance={capacityInstance} csrfToken={session.csrf_token} onClose={() => setCapacityInstance(null)} onSuccess={async () => { setNotice(t("已提交容量调整。", "Capacity change submitted.")); await refreshRuntime(); }} /> : null}
			{fleetAction&&runtime?<FleetOperationDialog action={fleetAction} instances={runtime.instances} csrfToken={session.csrf_token} onClose={()=>setFleetAction(null)} onSuccess={async()=>{setNotice(t("操作已提交，后台将持续核对结果。","Operation submitted; the background reconciler will confirm the result."));await refreshRuntime()}}/>:null}
		</div>
	);
}

function NavItem({
  to,
  label,
  description,
  icon,
  end = false
}: {
  to: string;
  label: string;
  description: string;
  icon: NavigationIconName;
  end?: boolean;
}) {
  return (
    <NavLink to={to} end={end} title={description} className={({ isActive }) => `nav-item${isActive ? " nav-item-active" : ""}`}>
      <NavigationIcon name={icon} />
      <strong>{label}</strong>
    </NavLink>
  );
}

function NavigationIcon({ name }: { name: NavigationIconName }) {
  const common = { width: 16, height: 16, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.7, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
  if (name === "search") return <svg {...common} aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5"/><path d="m16 16 5 5"/></svg>;
  if (name === "login") return <svg {...common}><path d="M14 4h5v16h-5M3 12h12m-4-4 4 4-4 4" /></svg>;
  if (name === "mail") return <svg {...common}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></svg>;
  if (name === "storage") return <svg {...common}><ellipse cx="12" cy="5" rx="8" ry="3" /><path d="M4 5v14c0 4 16 4 16 0V5M4 12c0 4 16 4 16 0" /></svg>;
  if (name === "applications") return <svg {...common}><rect x="3" y="3" width="7" height="7" rx="2" /><rect x="14" y="3" width="7" height="7" rx="2" /><rect x="3" y="14" width="7" height="7" rx="2" /><rect x="14" y="14" width="7" height="7" rx="2" /></svg>;
  if (name === "release") return <svg {...common}><path d="M12 16V3m-4 4 4-4 4 4M4 14v6h16v-6" /></svg>;
  if (name === "overview") return <svg {...common}><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><path d="M14 17.5h7M17.5 14v7" /></svg>;
  if (name === "monitoring") return <svg {...common}><path d="M3 12h4l2.2-6 4.2 12 2.2-6H21" /><path d="M4 4h16a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Z" /></svg>;
  if (name === "team") return <svg {...common}><circle cx="9" cy="8" r="3" /><path d="M3.5 20c.3-4 2.2-6 5.5-6s5.2 2 5.5 6M15 5.5c2.5.2 3.8 1.7 3.8 3.5S17.5 12.3 15 12.5M16.8 14.4c2.4.7 3.6 2.5 3.7 5.6" /></svg>;
  if (name === "sandbox") return <svg {...common}><path d="m12 3-9 5 9 5 9-5-9-5Z" /><path d="m3 13 9 5 9-5M3 18l9 5 9-5" /></svg>;
  if (name === "ids") return <svg {...common}><rect x="3" y="4" width="18" height="16" rx="3" /><circle cx="9" cy="10" r="2.5" /><path d="M5.8 16c.7-2 1.8-3 3.2-3s2.5 1 3.2 3M15 9h3M15 13h3" /></svg>;
  if (name === "model") return <svg {...common}><path d="M8 4h8M9 4v3m6-3v3M6 8h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2Z" /><circle cx="9" cy="13" r="1" /><circle cx="15" cy="13" r="1" /><path d="M9 17h6" /></svg>;
  if (name === "cluster") return <svg {...common}><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="8.5" y="14" width="7" height="7" rx="1.5" /><path d="M6.5 10v2h11v-2M12 12v2" /></svg>;
  return <svg {...common}><circle cx="12" cy="8" r="4" /><path d="M4.5 21c.5-5 3-7.5 7.5-7.5s7 2.5 7.5 7.5" /><path d="m18.5 5 .8 1.3 1.5.3-1 1.2.2 1.5-1.5-.6-1.4.6.2-1.5-1-1.2 1.5-.3.7-1.3Z" /></svg>;
}

function OverviewPage() {
  const { t } = useI18n();
  const { data } = useDashboard();
  if (!data) return null;
  const { overview, components } = data;
  const activeComponents = overview.components.total - overview.components.disabled;
  const healthyPercent = activeComponents > 0
    ? Math.round((overview.components.healthy / activeComponents) * 100)
    : 0;

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="OPERATIONS OVERVIEW"
        title={t(`${overview.data_center_id} 运行总览`, `${overview.data_center_id} operations overview`)}
        description={t("Control Center 直接读取各组件的管理与健康接口；单个组件异常不会阻断其它页面。", "Control Center reads each component's management and health API directly. A single component failure does not block other pages.")}
        status={<StatusBadge status={overview.process.ready ? "healthy" : "unavailable"} label={overview.process.ready ? "Control Center Ready" : "Control Center Not Ready"} />}
      />
      <section className="metric-grid" aria-label={t("组件状态汇总", "Component status summary") }>
        <MetricCard label={t("已纳管组件", "Managed components")} value={String(overview.components.total)} note={t(`${activeComponents} 个当前启用`, `${activeComponents} currently active`)} tone="blue" />
        <MetricCard label={t("健康组件", "Healthy components")} value={String(overview.components.healthy)} note={`${healthyPercent}% active healthy`} tone="green" />
        <MetricCard label={t("需要关注", "Needs attention")} value={String(overview.components.degraded + overview.components.unavailable)} note="degraded + unavailable" tone="orange" />
        <MetricCard label="Control Center" value={overview.release} note={t(`进程 ${overview.process.state}`, `process ${overview.process.state}`)} tone="neutral" />
      </section>
      <section className="panel">
        <div className="panel-header">
          <div>
            <span className="section-kicker">COMPONENTS</span>
            <h2>{t("数据中心组件", "Data center components")}</h2>
          </div>
          <span className="panel-meta">{t("观测时间", "Observed at")} {formatTime(components.observed_at)}</span>
        </div>
        <div className="component-list">
          {components.items.map((item) => <ComponentRow key={item.id} item={item} />)}
        </div>
      </section>
      <section className="boundary-note">
        <div className="boundary-index">01</div>
        <div>
          <strong>{t("管理面与数据面隔离", "Control plane and data plane are isolated")}</strong>
          <p>{t("这里展示和编排状态，但不会成为登录、Pacture 请求、AgentTask 或模型调用的流量跳点。", "This surface observes and orchestrates state without becoming a traffic hop for sign-in, Pacture requests, AgentTasks, or model calls.")}</p>
        </div>
      </section>
    </div>
  );
}

function ClusterPage() {
  const { t } = useI18n();
  const cluster = useComponent("kubernetes");
  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="CLUSTER FOUNDATION"
        title="Kubernetes Cluster"
        description={t("统一观察 EKS、k3s 或标准 Kubernetes provider；不向浏览器暴露 kubeconfig 或通用 Kubernetes 写入口。", "Observe EKS, k3s, or standard Kubernetes providers without exposing kubeconfig or a general-purpose write surface to the browser.")}
        status={cluster ? <StatusBadge status={cluster.status} /> : undefined}
      />
      {!cluster ? <MissingComponent title={t("Kubernetes observer 未注册", "Kubernetes observer is not registered")} /> : (
        <>
          <section className="metric-grid metric-grid-three">
            <MetricCard label="Provider" value={detailText(cluster, "provider", t("未配置", "Not configured"))} note="cluster adapter" tone="blue" />
            <MetricCard label="Server Version" value={detailText(cluster, "server_version", "—")} note={detailText(cluster, "platform", t("等待观测", "Waiting for observation"))} tone="neutral" />
            <MetricCard label="Ready Nodes" value={`${detailNumber(cluster, "ready_node_count")} / ${detailNumber(cluster, "node_count")}`} note="Node Ready condition" tone={cluster.status === "healthy" ? "green" : "orange"} />
          </section>
          <ComponentDetailPanel component={cluster} />
          {cluster.status === "disabled" ? (
            <SetupPanel
              title={t("连接 Kubernetes 只读状态", "Connect read-only Kubernetes status")}
              description={t("为 Control Center Pod 配置独立的只读 ServiceAccount、集群 CA 和 API endpoint，重新部署后本页会显示真实 Node 状态。", "Configure a dedicated read-only ServiceAccount, cluster CA, and API endpoint for the Control Center Pod. Redeploy to show live Node status.")}
              items={["CONTROL_CENTER_KUBERNETES_ENABLED=true", "Provider + API URL", "ServiceAccount token + CA file"]}
            />
          ) : null}
        </>
      )}
    </div>
  );
}

interface IDSDataCenterForm {
  publicOrigin: string;
  bridgeOrigin: string;
  displayName: string;
  lifecycle: IDSDataCenterInput["lifecycle"];
  capabilities: string;
  credentialAudience: string;
  trustBundleRevision: string;
  ingressReady: boolean;
}

const emptyIDSDataCenterForm: IDSDataCenterForm = {
  publicOrigin: "",
  bridgeOrigin: "",
  displayName: "",
  lifecycle: "provisioning",
  capabilities: "pacture, sandbox, tidb",
  credentialAudience: "",
  trustBundleRevision: "1",
  ingressReady: false
};

type DirectoryStatusTone = "active" | "pending" | "warning" | "disabled" | "failed";

function teamLifecycleCopy(lifecycle: IDSTeam["lifecycle"], t: Translator): { label: string; detail: string; tone: DirectoryStatusTone } {
  switch (lifecycle) {
    case "provisioning":
      return { label: t("准备中", "Provisioning"), detail: t("尚不可路由", "Not routable yet"), tone: "pending" };
    case "active":
      return { label: t("可用", "Active"), detail: t("允许路由", "Routable"), tone: "active" };
    case "disabled":
      return { label: t("已停用", "Disabled"), detail: t("暂不可访问", "Temporarily unavailable"), tone: "disabled" };
    case "tombstoned":
      return { label: t("已删除", "Deleted"), detail: t("Team ID 不再复用", "Team ID cannot be reused"), tone: "failed" };
  }
}

function registrationStateCopy(state: IDSTeamRegistration["state"], t: Translator): { label: string; tone: DirectoryStatusTone } {
  switch (state) {
    case "requested": return { label: t("等待处理", "Queued"), tone: "pending" };
    case "running": return { label: t("创建中", "In progress"), tone: "warning" };
    case "ready": return { label: t("创建完成", "Complete"), tone: "active" };
    case "failed": return { label: t("创建失败", "Failed"), tone: "failed" };
  }
}


function dataCenterStatusCopy(lifecycle: IDSConfiguration["data_centers"][number]["lifecycle"], ingressReady: boolean, t: Translator): { label: string; tone: DirectoryStatusTone } {
  if (lifecycle === "active" && ingressReady) return { label: t("可接入", "Available"), tone: "active" };
  if (lifecycle === "active") return { label: t("入口未就绪", "Ingress not ready"), tone: "warning" };
  if (lifecycle === "provisioning") return { label: t("准备中", "Provisioning"), tone: "pending" };
  if (lifecycle === "draining") return { label: t("排空中", "Draining"), tone: "warning" };
  return { label: t("已停用", "Disabled"), tone: "disabled" };
}

function DirectorySortHeader({
  field,
  label,
  activeSort,
  direction,
  onSort
}: {
  field: IDSTeamSort;
  label: string;
  activeSort: IDSTeamSort;
  direction: SortDirection;
  onSort: (field: IDSTeamSort) => void;
}) {
  const active = field === activeSort;
  return (
    <th aria-sort={active ? (direction === "asc" ? "ascending" : "descending") : "none"}>
      <button className={`directory-sort${active ? " directory-sort-active" : ""}`} type="button" onClick={() => onSort(field)}>
        {label}<span aria-hidden="true">{active ? (direction === "asc" ? "↑" : "↓") : "↕"}</span>
      </button>
    </th>
  );
}

function IDSPage() {
  const { t } = useI18n();
  const { data, session } = useDashboard();
  const dataCenterID = data?.overview.data_center_id ?? "";
  const component = useComponent("ids");
  const [configuration, setConfiguration] = useState<IDSConfiguration | null>(null);
  const [teams, setTeams] = useState<IDSTeam[]>([]);
  const [teamTotal, setTeamTotal] = useState(0);
  const [teamPage, setTeamPage] = useState(1);
  const [teamPageSize, setTeamPageSize] = useState(20);
  const [teamSearchInput, setTeamSearchInput] = useState("");
  const [teamSearch, setTeamSearch] = useState("");
  const [teamDataCenterFilter, setTeamDataCenterFilter] = useState("");
  const [teamLifecycleFilter, setTeamLifecycleFilter] = useState<"" | IDSTeam["lifecycle"]>("");
  const [teamSort, setTeamSort] = useState<IDSTeamSort>("updated_at");
  const [teamSortDirection, setTeamSortDirection] = useState<SortDirection>("desc");
  const [teamsLoading, setTeamsLoading] = useState(true);
  const [teamDirectoryError, setTeamDirectoryError] = useState("");
  const [teamRegistrations, setTeamRegistrations] = useState<IDSTeamRegistration[]>([]);
  const [operations, setOperations] = useState<IDSOperation[]>([]);
  const [policyInput, setPolicyInput] = useState({ membershipLimit: "16", manifestTTL: "900" });
  const [dataCenterInput, setDataCenterInput] = useState<IDSDataCenterForm>(emptyIDSDataCenterForm);
  const [reason, setReason] = useState("");
  const [retryTarget, setRetryTarget] = useState<IDSTeamRegistration | null>(null);
  const [retryReason, setRetryReason] = useState("");
  const [retryError, setRetryError] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [section, setSection] = useState<"directory" | "registrations" | "configuration" | "operations">("directory");
  const [idsQuery, setIDSQuery] = useSearchParams();
  useEffect(() => { if (idsQuery.has("registration_id")) setSection("registrations"); }, [idsQuery]);

  const refreshIDS = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [nextConfiguration, nextTeamRegistrations, nextOperations] = await Promise.all([
        getIDSConfiguration(),
        listIDSTeamRegistrations(),
        listIDSOperations()
      ]);
      setConfiguration(nextConfiguration);
      setTeamRegistrations(nextTeamRegistrations);
      setOperations(nextOperations);
      setPolicyInput({
        membershipLimit: String(nextConfiguration.directory_policy.membership_limit_count),
        manifestTTL: String(nextConfiguration.directory_policy.route_manifest_ttl_seconds)
      });
      const localDataCenter = nextConfiguration.data_centers.find((item) => item.data_center_id === dataCenterID);
      setDataCenterInput(localDataCenter ? {
        publicOrigin: localDataCenter.public_origin,
        bridgeOrigin: localDataCenter.bridge_origin ?? "",
        displayName: localDataCenter.display_name,
        lifecycle: localDataCenter.lifecycle,
        capabilities: localDataCenter.capabilities.join(", "),
        credentialAudience: localDataCenter.credential_audience,
        trustBundleRevision: String(localDataCenter.trust_bundle_revision),
        ingressReady: localDataCenter.ingress_ready
      } : {
        ...emptyIDSDataCenterForm,
        credentialAudience: dataCenterID ? `pacture-dc:${dataCenterID}` : ""
      });
    } catch (caught) {
      setError(authenticationError(caught, t));
    } finally {
      setLoading(false);
    }
  }, [dataCenterID]);

  useEffect(() => {
    void refreshIDS();
  }, [refreshIDS]);

  const teamQuery = useMemo(() => ({
    page: teamPage,
    page_size: teamPageSize,
    search: teamSearch || undefined,
    data_center_id: teamDataCenterFilter || undefined,
    lifecycle: teamLifecycleFilter || undefined,
    sort: teamSort,
    direction: teamSortDirection
  }), [teamDataCenterFilter, teamLifecycleFilter, teamPage, teamPageSize, teamSearch, teamSort, teamSortDirection]);

  const refreshTeams = useCallback(async () => {
    setTeamsLoading(true);
    setTeamDirectoryError("");
    try {
      const nextPage = await listIDSTeams(teamQuery);
      setTeams(nextPage.items);
      setTeamTotal(nextPage.total);
    } catch (caught) {
      setTeamDirectoryError(authenticationError(caught, t));
    } finally {
      setTeamsLoading(false);
    }
  }, [t, teamQuery]);

  useEffect(() => {
    void refreshTeams();
  }, [refreshTeams]);

  function applyTeamSearch(event: FormEvent) {
    event.preventDefault();
    setTeamPage(1);
    setTeamSearch(teamSearchInput.trim());
  }

  function resetTeamFilters() {
    setTeamSearchInput("");
    setTeamSearch("");
    setTeamDataCenterFilter("");
    setTeamLifecycleFilter("");
    setTeamSort("updated_at");
    setTeamSortDirection("desc");
    setTeamPage(1);
  }

  function updateTeamSort(nextSort: IDSTeamSort) {
    if (nextSort === teamSort) {
      setTeamSortDirection((current) => current === "asc" ? "desc" : "asc");
    } else {
      setTeamSort(nextSort);
      setTeamSortDirection(nextSort === "display_name" || nextSort === "team_id" ? "asc" : "desc");
    }
    setTeamPage(1);
  }

  async function submitPolicy(event: FormEvent) {
    event.preventDefault();
    if (!configuration || !reason.trim()) {
      setError(t("变更 IDS 配置必须填写原因", "Enter a reason before changing IDS configuration"));
      return;
    }
    const input: IDSDirectoryPolicyInput = {
      expected_revision: configuration.directory_policy.revision,
      membership_limit_count: Number(policyInput.membershipLimit),
      route_manifest_ttl_seconds: Number(policyInput.manifestTTL)
    };
    if (!Number.isInteger(input.membership_limit_count) || !Number.isInteger(input.route_manifest_ttl_seconds)) {
      setError(t("策略值必须是整数", "Policy values must be integers"));
      return;
    }
    setSubmitting(true);
    setError("");
    setNotice("");
    try {
      const updated = await updateIDSDirectoryPolicy(session.csrf_token, reason.trim(), input);
      setNotice(t(`IDS 目录策略已更新到 revision ${updated.revision}。`, `IDS directory policy was updated to revision ${updated.revision}.`));
      setReason("");
      await refreshIDS();
    } catch (caught) {
      setError(authenticationError(caught, t));
    } finally {
      setSubmitting(false);
    }
  }

  function openTeamRegistrationRetry(operation: IDSTeamRegistration) {
    setRetryTarget(operation);
    setRetryReason("");
    setRetryError("");
    setError("");
    setNotice("");
  }

  function closeTeamRegistrationRetry() {
    if (submitting) return;
    setRetryTarget(null);
    setRetryReason("");
    setRetryError("");
  }

  async function submitTeamRegistrationRetry(event: FormEvent) {
    event.preventDefault();
    if (!retryTarget) return;
    const normalizedReason = retryReason.trim();
    if (!normalizedReason) {
      setRetryError(t("请说明重试原因；该内容将写入审计记录。", "Describe why this retry is needed; the reason will be recorded in the audit log."));
      return;
    }
    setSubmitting(true);
    setRetryError("");
    setNotice("");
    try {
      await retryIDSTeamRegistration(
        session.csrf_token,
        normalizedReason,
        retryTarget.operation_id,
        retryTarget.revision
      );
      setNotice(t(`Team ${retryTarget.team_id} 的创建重试已下发。`, `Creation retry was submitted for Team ${retryTarget.team_id}.`));
      setRetryTarget(null);
      setRetryReason("");
      await refreshIDS();
    } catch (caught) {
      setRetryError(authenticationError(caught, t));
    } finally {
      setSubmitting(false);
    }
  }

  async function submitDataCenter(event: FormEvent) {
    event.preventDefault();
    if (!configuration || !reason.trim()) {
      setError(t("变更 IDS 配置必须填写原因", "Enter a reason before changing IDS configuration"));
      return;
    }
    const current = configuration.data_centers.find((item) => item.data_center_id === dataCenterID);
    const input: IDSDataCenterInput = {
      expected_revision: current?.revision ?? 0,
      public_origin: dataCenterInput.publicOrigin.trim(),
      static_origin: current?.static_origin ?? "",
      bridge_origin: dataCenterInput.bridgeOrigin.trim(),
      display_name: dataCenterInput.displayName.trim(),
      lifecycle: dataCenterInput.lifecycle,
      capabilities: dataCenterInput.capabilities.split(",").map((item) => item.trim()).filter(Boolean),
      credential_audience: dataCenterInput.credentialAudience.trim(),
      trust_bundle_revision: Number(dataCenterInput.trustBundleRevision),
      ingress_ready: dataCenterInput.ingressReady
    };
    if (!Number.isInteger(input.trust_bundle_revision)) {
      setError(t("Trust bundle revision 必须是整数", "Trust bundle revision must be an integer"));
      return;
    }
    setSubmitting(true);
    setError("");
    setNotice("");
    try {
      const updated = await upsertIDSDataCenter(session.csrf_token, reason.trim(), input);
      setNotice(t(`${updated.data_center_id} 已更新到 revision ${updated.revision}。`, `${updated.data_center_id} was updated to revision ${updated.revision}.`));
      setReason("");
      await refreshIDS();
    } catch (caught) {
      setError(authenticationError(caught, t));
    } finally {
      setSubmitting(false);
    }
  }

  const policy = configuration?.directory_policy;
  const activeDataCenters = configuration?.data_centers.filter((item) => item.lifecycle === "active" && item.ingress_ready).length ?? 0;
  const teamPageCount = Math.max(1, Math.ceil(teamTotal / teamPageSize));
  const teamRangeStart = teamTotal === 0 ? 0 : (teamPage - 1) * teamPageSize + 1;
  const teamRangeEnd = Math.min(teamPage * teamPageSize, teamTotal);
  const hasTeamFilters = Boolean(teamSearchInput || teamSearch || teamDataCenterFilter || teamLifecycleFilter);

  useEffect(() => {
    if (teamPage > teamPageCount) setTeamPage(teamPageCount);
  }, [teamPage, teamPageCount]);

  useEffect(() => {
    if (!retryTarget) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !submitting) {
        setRetryTarget(null);
        setRetryReason("");
        setRetryError("");
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [retryTarget, submitting]);

  function refreshAllIDSData() {
    void Promise.all([refreshIDS(), refreshTeams()]);
  }

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="GLOBAL IDENTITY DIRECTORY"
        title="Identity Directory Service"
        description={t("Control Center 通过 IDS 管理 API 读取并下发配置。有效配置由 IDS 自己持久化和加载，Control Center 不是运行依赖。", "Control Center reads and applies configuration through the IDS management API. IDS persists and loads effective configuration independently; Control Center is not a runtime dependency.")}
        status={component ? <StatusBadge status={component.status} /> : undefined}
      />
      {error ? <div className="global-error administrator-message" role="alert">{error}</div> : null}
      {notice ? <div className="success-message" role="status">{notice}</div> : null}
          <PageTabs
            value={section}
            onChange={setSection}
            items={[
              { value: "directory", label: t("Team 路由目录", "Team route directory"), count: teamTotal },
              { value: "registrations", label: t("Team 创建过程", "Team creation"), count: teamRegistrations.length },
              { value: "configuration", label: t("配置", "Configuration") },
              { value: "operations", label: t("人工操作", "Manual operations"), count: operations.length }
            ]}
          />
          {section === "registrations" ? <RegistrationLookup csrfToken={session.csrf_token} /> : null}
      {loading && !configuration ? <LoadingState /> : configuration ? (
        <>
          <section className="metric-grid">
            <MetricCard label={t("注册 Team 上限", "Team registration limit")} value={String(policy?.registration_limit_count ?? "—")} note={t("固定产品策略", "Fixed product policy")} tone="blue" />
            <MetricCard label={t("加入 Team 上限", "Team membership limit")} value={String(policy?.membership_limit_count ?? "—")} note={`policy revision ${policy?.revision ?? "—"}`} tone="neutral" />
            <MetricCard label={t("可用 Team", "Active Teams")} value={component ? detailNumber(component, "active_teams") : String(teamTotal)} note={t("允许路由的 Team", "Teams currently routable")} tone="green" />
            <MetricCard label={t("创建异常", "Creation failures")} value={component ? detailNumber(component, "failed_team_registrations") : "—"} note={t("可从创建过程重试", "Retry from Team creation")} tone={component?.status === "degraded" ? "orange" : "neutral"} />
          </section>

          <section className="ids-data-centers" aria-labelledby="ids-data-centers-title">
            <div className="ids-data-centers-header">
              <div>
                <span className="section-kicker">DATA CENTER REGISTRY</span>
                <h2 id="ids-data-centers-title">{t("数据中心", "Data centers")}</h2>
                <p>{t(`${activeDataCenters} 个可接入，共 ${configuration.data_centers.length} 个已注册`, `${activeDataCenters} available of ${configuration.data_centers.length} registered`)}</p>
              </div>
              <button className="secondary-button" type="button" onClick={refreshAllIDSData} disabled={loading || teamsLoading || submitting}>{t("刷新目录", "Refresh directory")}</button>
            </div>
            <div className="ids-data-center-grid">
              {configuration.data_centers.map((item) => {
                const status = dataCenterStatusCopy(item.lifecycle, item.ingress_ready, t);
                const isCurrent = item.data_center_id === dataCenterID;
                return <article className={`ids-data-center-card${isCurrent ? " ids-data-center-current" : ""}`} key={item.data_center_id}>
                  <div className="ids-data-center-card-header">
                    <div><strong>{item.display_name}</strong><span>{item.data_center_id}</span></div>
                    <span className={`user-state user-state-${status.tone}`}>{status.label}</span>
                  </div>
                  <p title={item.public_origin}>{item.public_origin}</p>
                  <div className="ids-data-center-meta">
                    {isCurrent ? <span className="ids-current-label">{t("当前管理面", "Current control plane")}</span> : null}
                    <span>rev {item.revision}</span>
                    <span>trust {item.trust_bundle_revision}</span>
                  </div>
                  <div className="ids-capabilities" aria-label={t("数据中心能力", "Data-center capabilities")}>
                    {item.capabilities.map((capability) => <span key={capability}>{capability}</span>)}
                  </div>
                </article>;
              })}
            </div>
          </section>



          {section === "registrations" && component ? <section className="metric-grid">
            <MetricCard label={t("创建中的 Team", "Teams being created")} value={detailNumber(component, "pending_team_registrations")} note={t(`失败 ${detailNumber(component, "failed_team_registrations")}`, `failed ${detailNumber(component, "failed_team_registrations")}`)} tone={component.status === "degraded" ? "orange" : "neutral"} />
            <MetricCard label="IDS Outbox" value={detailNumber(component, "pending_outbox_events")} note={t("等待投递事件", "Events waiting for delivery")} tone="neutral" />
            <MetricCard label={t("密码找回 SMTP", "Password recovery SMTP")} value={detailBooleanValue(component, "password_recovery_configured") ? detailBoolean(component, "password_recovery_available", t) : t("未配置", "Not configured")} note={t("TLS、认证与发件人探测", "TLS, authentication, and sender probe")} tone={detailBooleanValue(component, "password_recovery_configured") ? (detailBooleanValue(component, "password_recovery_available") ? "green" : "orange") : "neutral"} />
            <MetricCard label={t("身份邮件", "Identity email")} value={detailNumber(component, "pending_email_deliveries")} note={t(`重试中 ${detailNumber(component, "retrying_email_deliveries")} · 失败 ${detailNumber(component, "failed_email_deliveries")}`, `retrying ${detailNumber(component, "retrying_email_deliveries")} · failed ${detailNumber(component, "failed_email_deliveries")}`)} tone={detailNumber(component, "retrying_email_deliveries") === "0" && detailNumber(component, "failed_email_deliveries") === "0" ? "neutral" : "orange"} />
            <MetricCard label={t("加入配额预留", "Membership reservations")} value={detailNumber(component, "active_membership_reservations")} note={t(`已使用 ${detailNumber(component, "membership_quota_used")}`, `used ${detailNumber(component, "membership_quota_used")}`)} tone="blue" />
            <MetricCard label={t("过期配额预留", "Expired reservations")} value={detailNumber(component, "expired_membership_reservations")} note={t(`账面 reserved ${detailNumber(component, "membership_quota_reserved")}`, `recorded reserved ${detailNumber(component, "membership_quota_reserved")}`)} tone={detailNumber(component, "expired_membership_reservations") === "0" ? "green" : "orange"} />
          </section> : null}

          {section === "configuration" ? <section className="configuration-grid">
            <form className="panel configuration-form" onSubmit={submitPolicy}>
              <div className="panel-header">
                <div><span className="section-kicker">DIRECTORY POLICY</span><h2>{t("全局目录策略", "Global directory policy")}</h2></div>
                <span className="read-only-label">CAS revision {policy?.revision}</span>
              </div>
              <div className="configuration-form-body">
                <label>{t("每个用户可注册 Team 数", "Teams each user may register")}<input value="1" disabled /></label>
                <label>{t("每个用户最多加入 Team 数", "Maximum Teams per user")}<input type="number" min="1" max="1000" value={policyInput.membershipLimit} onChange={(event) => setPolicyInput({ ...policyInput, membershipLimit: event.target.value })} /></label>
                <label>{t("Route Manifest TTL（秒）", "Route Manifest TTL (seconds)")}<input type="number" min="60" max="86400" value={policyInput.manifestTTL} onChange={(event) => setPolicyInput({ ...policyInput, manifestTTL: event.target.value })} /></label>
                <label>{t("变更原因", "Change reason")}<input value={reason} onChange={(event) => setReason(event.target.value)} placeholder={t("必填，将写入双侧审计", "Required and written to both audit logs")} /></label>
                <button className="primary-button" type="submit" disabled={submitting}>{t("保存目录策略", "Save directory policy")}</button>
              </div>
            </form>

            <form className="panel configuration-form" onSubmit={submitDataCenter}>
              <div className="panel-header">
                <div><span className="section-kicker">LOCAL DATA CENTER</span><h2>{dataCenterID || t("当前数据中心", "Current data center")}</h2></div>
                <span className="read-only-label">IDS Registry</span>
              </div>
              <div className="configuration-form-body configuration-form-two-column">
                <label>Bridge origin<input value={dataCenterInput.bridgeOrigin} onChange={(event) => setDataCenterInput({ ...dataCenterInput, bridgeOrigin: event.target.value })} placeholder="https://bridge.example.com" /></label>
                <label>Public origin<input value={dataCenterInput.publicOrigin} onChange={(event) => setDataCenterInput({ ...dataCenterInput, publicOrigin: event.target.value })} placeholder="https://dc1.ones.ai" /></label>
                <label>{t("显示名称", "Display name")}<input value={dataCenterInput.displayName} onChange={(event) => setDataCenterInput({ ...dataCenterInput, displayName: event.target.value })} /></label>
                <label>{t("数据中心状态", "Data-center status")}<select value={dataCenterInput.lifecycle} onChange={(event) => setDataCenterInput({ ...dataCenterInput, lifecycle: event.target.value as IDSDataCenterInput["lifecycle"] })}><option value="provisioning">{t("准备中", "Provisioning")}</option><option value="active">{t("可用", "Active")}</option><option value="draining">{t("排空中", "Draining")}</option><option value="disabled">{t("已停用", "Disabled")}</option></select></label>
                <label>Capabilities<input value={dataCenterInput.capabilities} onChange={(event) => setDataCenterInput({ ...dataCenterInput, capabilities: event.target.value })} /></label>
                <label>Credential audience<input value={dataCenterInput.credentialAudience} onChange={(event) => setDataCenterInput({ ...dataCenterInput, credentialAudience: event.target.value })} /></label>
                <label>Trust bundle revision<input type="number" min="1" value={dataCenterInput.trustBundleRevision} onChange={(event) => setDataCenterInput({ ...dataCenterInput, trustBundleRevision: event.target.value })} /></label>
                <label className="checkbox-label"><input type="checkbox" checked={dataCenterInput.ingressReady} onChange={(event) => setDataCenterInput({ ...dataCenterInput, ingressReady: event.target.checked })} />Ingress ready</label>
                <label>{t("变更原因", "Change reason")}<input value={reason} onChange={(event) => setReason(event.target.value)} placeholder={t("与上方共用", "Shared with the form above")} /></label>
                <button className="primary-button configuration-submit-wide" type="submit" disabled={submitting}>{t("下发当前数据中心配置", "Apply current data-center configuration")}</button>
              </div>
            </form>
          </section> : null}


          {section === "registrations" ? <section className="panel">
            <div className="panel-header"><div><span className="section-kicker">TEAM REGISTRATION SAGA</span><h2>{t("Team 创建过程", "Team creation process")}</h2></div><span className="panel-meta">{t("IDS 持久化状态，可追踪失败阶段", "IDS-persisted state with traceable failure stages")}</span></div>
            {teamRegistrations.length === 0 ? <div className="administrator-loading">{t("尚无 Team 创建记录。", "No Team creation records yet.")}</div> : <div className="administrator-table-wrap"><table className="administrator-table registration-table"><thead><tr><th>Team</th><th>{t("数据中心", "Data center")}</th><th>{t("创建状态", "Creation status")}</th><th>{t("当前阶段", "Current stage")}</th><th>{t("更新时间", "Updated")}</th><th>{t("操作", "Actions")}</th></tr></thead><tbody>{teamRegistrations.map((operation) => {
              const state = registrationStateCopy(operation.state, t);
              return <tr key={operation.operation_id}><td><strong>{operation.team_id}</strong><span>{operation.operation_id}</span></td><td>{operation.data_center_id}</td><td><span className={`user-state user-state-${state.tone}`}>{state.label}</span>{operation.error_code ? <small className="registration-error">{operation.error_code}<br />{<DiagnosticText value={{diagnostic:{code:operation.error_code},message:operation.error_message}}/>}</small> : null}</td><td><strong>{registrationStepLabel(operation.step, t)}</strong><span>{t(`第 ${operation.attempt_count} 次尝试`, `Attempt ${operation.attempt_count}`)} · rev {operation.revision}</span></td><td>{formatDateTime(operation.updated_at)}</td><td><button className="secondary-button" type="button" onClick={() => setIDSQuery({registration_id: operation.operation_id})}>{t("查看详情", "View details")}</button>{operation.state === "failed" ? <button className="secondary-button" type="button" disabled={submitting} onClick={() => openTeamRegistrationRetry(operation)}>{t("重试创建", "Retry creation")}</button> : <span>—</span>}</td></tr>;
            })}</tbody></table></div>}
          </section> : null}

          {section === "directory" ? <section className="panel">
            <div className="panel-header"><div><span className="section-kicker">TEAM ROUTE DIRECTORY</span><h2>{t("Team 路由目录", "Team route directory")}</h2></div><span className="panel-meta">{teamsLoading ? t("正在更新…", "Updating…") : t("用于发现和路由，不授予 Team 权限", "Used for discovery and routing; does not grant Team access")}</span></div>
            <form className="directory-toolbar" onSubmit={applyTeamSearch}>
              <label className="directory-search">
                <span className="visually-hidden">{t("搜索 Team", "Search Teams")}</span>
                <input value={teamSearchInput} onChange={(event) => setTeamSearchInput(event.target.value)} placeholder={t("搜索 Team ID、名称或 Owner ID", "Search Team ID, name, or Owner ID")} maxLength={128} />
              </label>
              <label>
                <span>{t("数据中心", "Data center")}</span>
                <select value={teamDataCenterFilter} onChange={(event) => { setTeamDataCenterFilter(event.target.value); setTeamPage(1); }}>
                  <option value="">{t("全部数据中心", "All data centers")}</option>
                  {configuration.data_centers.map((item) => <option value={item.data_center_id} key={item.data_center_id}>{item.display_name}</option>)}
                </select>
              </label>
              <label>
                <span>{t("Team 状态", "Team status")}</span>
                <select value={teamLifecycleFilter} onChange={(event) => { setTeamLifecycleFilter(event.target.value as "" | IDSTeam["lifecycle"]); setTeamPage(1); }}>
                  <option value="">{t("全部状态", "All statuses")}</option>
                  <option value="active">{t("可用", "Active")}</option>
                  <option value="provisioning">{t("准备中", "Provisioning")}</option>
                  <option value="disabled">{t("已停用", "Disabled")}</option>
                  <option value="tombstoned">{t("已删除", "Deleted")}</option>
                </select>
              </label>
              <button className="primary-button directory-search-button" type="submit" disabled={teamsLoading}>{t("查询", "Search")}</button>
              <button className="secondary-button directory-reset-button" type="button" onClick={resetTeamFilters} disabled={!hasTeamFilters || teamsLoading}>{t("清除筛选", "Clear filters")}</button>
            </form>
            {teamDirectoryError ? <div className="global-error directory-message" role="alert">{teamDirectoryError}</div> : null}
            {teamsLoading && teams.length === 0 ? <div className="administrator-loading">{t("正在读取 Team 路由目录…", "Loading Team route directory…")}</div> : teams.length === 0 ? <div className="administrator-loading">{hasTeamFilters ? t("没有符合筛选条件的 Team。", "No Teams match the current filters.") : t("Team 路由目录为空。", "The Team route directory is empty.")}</div> : <div className="administrator-table-wrap"><table className="administrator-table directory-table" aria-busy={teamsLoading}><thead><tr>
              <DirectorySortHeader field="display_name" label={t("Team", "Team")} activeSort={teamSort} direction={teamSortDirection} onSort={updateTeamSort} />
              <DirectorySortHeader field="home_data_center_id" label={t("归属数据中心", "Home data center")} activeSort={teamSort} direction={teamSortDirection} onSort={updateTeamSort} />
              <DirectorySortHeader field="lifecycle" label={t("Team 状态", "Team status")} activeSort={teamSort} direction={teamSortDirection} onSort={updateTeamSort} />
              <DirectorySortHeader field="owner_user_id" label="Owner ID" activeSort={teamSort} direction={teamSortDirection} onSort={updateTeamSort} />
              <DirectorySortHeader field="route_revision" label={t("路由版本", "Route revision")} activeSort={teamSort} direction={teamSortDirection} onSort={updateTeamSort} />
              <DirectorySortHeader field="updated_at" label={t("更新时间", "Updated") } activeSort={teamSort} direction={teamSortDirection} onSort={updateTeamSort} />
            </tr></thead><tbody>{teams.map((team) => {
              const lifecycle = teamLifecycleCopy(team.lifecycle, t);
              const dataCenter = configuration.data_centers.find((item) => item.data_center_id === team.home_data_center_id);
              return <tr key={team.team_id}>
                <td><strong>{team.display_name}</strong><span>{team.team_id}</span></td>
                <td><strong>{dataCenter?.display_name ?? team.home_data_center_id}</strong><small>{team.home_data_center_id}</small></td>
                <td><span className={`user-state user-state-${lifecycle.tone}`}>{lifecycle.label}</span><small>{lifecycle.detail}</small></td>
                <td className="directory-monospace" title={team.owner_user_id}>{team.owner_user_id}</td>
                <td className="directory-revision">{team.route_revision}</td>
                <td><strong>{formatDateTime(team.updated_at)}</strong><small>{t("创建于", "Created")} {formatDateTime(team.created_at)}</small></td>
              </tr>;
            })}</tbody></table></div>}
            <div className="directory-pagination" aria-label={t("Team 目录分页", "Team directory pagination")}>
              <span>{t(`显示 ${teamRangeStart}–${teamRangeEnd}，共 ${teamTotal} 个 Team`, `Showing ${teamRangeStart}–${teamRangeEnd} of ${teamTotal} Teams`)}</span>
              <div>
                <label>{t("每页", "Rows per page")}<select value={teamPageSize} onChange={(event) => { setTeamPageSize(Number(event.target.value)); setTeamPage(1); }}><option value="20">20</option><option value="50">50</option><option value="100">100</option></select></label>
                <button className="secondary-button" type="button" disabled={teamPage <= 1 || teamsLoading} onClick={() => setTeamPage((current) => Math.max(1, current - 1))}>{t("上一页", "Previous")}</button>
                <strong>{teamPage} / {teamPageCount}</strong>
                <button className="secondary-button" type="button" disabled={teamPage >= teamPageCount || teamsLoading} onClick={() => setTeamPage((current) => Math.min(teamPageCount, current + 1))}>{t("下一页", "Next")}</button>
              </div>
            </div>
          </section> : null}

          {section === "operations" ? <section className="panel">
            <div className="panel-header"><div><span className="section-kicker">AUDITED OPERATIONS</span><h2>{t("最近 IDS 配置操作", "Recent IDS configuration operations")}</h2></div><span className="panel-meta">{t("保存在 Control Center TiDB", "Stored in Control Center TiDB")}</span></div>
            {operations.length === 0 ? <div className="administrator-loading">{t("尚无 IDS 配置操作。", "No IDS configuration operations yet.")}</div> : <div className="administrator-table-wrap"><table className="administrator-table"><thead><tr><th>{t("操作", "Action")}</th><th>{t("原因", "Reason")}</th><th>{t("结果", "Result")}</th><th>{t("时间", "Time")}</th></tr></thead><tbody>{operations.map((operation) => <tr key={operation.operation_id}><td><strong>{operation.action}</strong><span>{operation.target_id}</span></td><td>{operation.reason}</td><td><span className={`user-state user-state-${operation.state === "succeeded" ? "active" : "disabled"}`}>{operation.state}</span>{operation.error_code ? <small>{operation.error_code}</small> : null}</td><td>{formatDateTime(operation.created_at)}</td></tr>)}</tbody></table></div>}
          </section> : null}

          {retryTarget ? (
            <div className="dialog-backdrop" role="presentation" onMouseDown={(event) => {
              if (event.target === event.currentTarget) closeTeamRegistrationRetry();
            }}>
              <section className="action-dialog retry-registration-dialog" role="dialog" aria-modal="true" aria-labelledby="retry-registration-title" aria-describedby="retry-registration-description">
                <span className="section-kicker">RETRY TEAM REGISTRATION</span>
                <h2 id="retry-registration-title">{t(`重试创建 Team ${retryTarget.team_id}`, `Retry creation for Team ${retryTarget.team_id}`)}</h2>
                <p id="retry-registration-description">{t("确认后，IDS 会从已记录的失败阶段重新处理该创建操作。重试原因将与操作者身份一起写入审计记录。", "IDS will resume this creation operation from its recorded failure stage. The retry reason and administrator identity will be written to the audit log.")}</p>
                <dl className="retry-registration-summary">
                  <div><dt>{t("失败阶段", "Failed stage")}</dt><dd>{registrationStepLabel(retryTarget.step, t)}</dd></div>
                  <div><dt>{t("数据中心", "Data center")}</dt><dd>{retryTarget.data_center_id}</dd></div>
                  <div><dt>{t("已尝试", "Attempts")}</dt><dd>{retryTarget.attempt_count}</dd></div>
                  <div><dt>{t("当前版本", "Current revision")}</dt><dd>{retryTarget.revision}</dd></div>
                </dl>
                {retryTarget.error_code || retryTarget.error_message ? <div className="retry-registration-failure">
                  <strong>{retryTarget.error_code || t("创建失败", "Creation failed")}</strong>
                  {retryTarget.error_message ? <span>{<DiagnosticText value={{diagnostic:{code:retryTarget.error_code||"DIAGNOSTIC_UNAVAILABLE"},message:retryTarget.error_message}}/>}</span> : null}
                </div> : null}
                <form onSubmit={submitTeamRegistrationRetry}>
                  <label htmlFor="retry-registration-reason">{t("重试原因", "Retry reason")}</label>
                  <textarea
                    id="retry-registration-reason"
                    value={retryReason}
                    onChange={(event) => {
                      setRetryReason(event.target.value);
                      if (retryError) setRetryError("");
                    }}
                    maxLength={512}
                    rows={4}
                    placeholder={t("例如：TiDB 连接已经恢复，重新执行数据库创建", "For example: TiDB connectivity has recovered; retry database creation")}
                    autoFocus
                  />
                  <div className="retry-reason-meta"><span>{t("必填，将写入审计记录", "Required and recorded in the audit log")}</span><span>{retryReason.length}/512</span></div>
                  {retryError ? <div className="retry-registration-form-error" role="alert">{retryError}</div> : null}
                  <div className="dialog-actions">
                    <button className="secondary-button" type="button" onClick={closeTeamRegistrationRetry} disabled={submitting}>{t("取消", "Cancel")}</button>
                    <button className="primary-button" type="submit" disabled={submitting}>{submitting ? t("正在提交…", "Submitting…") : t("确认重试", "Confirm retry")}</button>
                  </div>
                </form>
              </section>
            </div>
          ) : null}
        </>
      ) : <MissingComponent title={t("无法读取 IDS 配置", "Unable to load IDS configuration")} />}
    </div>
  );
}

function LLMGatewayPage() {
  const { t } = useI18n();
  const { session } = useDashboard();
  const gateway = useComponent("llm-gateway");
  const [providers, setProviders] = useState<LLMGatewayProvider[]>([]);
  const [operations, setOperations] = useState<IDSOperation[]>([]);
  const [section, setSection] = useState<"providers" | "operations">("providers");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const refreshProviders = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [nextProviders, nextOperations] = await Promise.all([
        listLLMGatewayProviders(),
        listLLMGatewayOperations()
      ]);
      setProviders(nextProviders);
      setOperations(nextOperations);
    } catch (caught) {
      setError(authenticationError(caught, t));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void refreshProviders();
  }, [refreshProviders]);

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="RUNTIME INFRASTRUCTURE"
        title="LLM Gateway"
        description={t("Control Center 通过 LLM Gateway owner 的受限管理 API 配置上游模型；master key 留在 owner，Control Center 不进入模型调用链路。", "Control Center configures upstream models through the LLM Gateway owner's scoped API. The master key remains with the owner and Control Center is not part of model-call traffic.")}
        status={gateway ? <StatusBadge status={gateway.status} /> : undefined}
      />
      {error ? <div className="global-error administrator-message" role="alert">{error}</div> : null}
      {!gateway ? <MissingComponent title={t("LLM Gateway observer 未注册", "LLM Gateway observer is not registered")} /> : (
        <>
          <section className="metric-grid metric-grid-three">
            <MetricCard label="Liveliness" value={detailBoolean(gateway, "live", t)} note="LiteLLM process" tone={detailBooleanValue(gateway, "live") ? "green" : "orange"} />
            <MetricCard label="Readiness" value={detailBoolean(gateway, "ready", t)} note="model gateway readiness" tone={detailBooleanValue(gateway, "ready") ? "green" : "orange"} />
            <MetricCard label={t("已配置模型", "Configured models")} value={String(providers.length)} note={t("API Key 仅写入 owner", "API keys are written only to the owner")} tone="blue" />
          </section>
          <ComponentDetailPanel component={gateway} />
          <PageTabs value={section} onChange={setSection} items={[
            { value: "providers", label: t("模型供应商", "Model providers"), count: providers.length },
            { value: "operations", label: t("人工操作", "Manual operations"), count: operations.length }
          ]} />
          {section === "providers" ? <LLMGatewayProviders
            csrfToken={session.csrf_token}
            loading={loading}
            providers={providers}
            refresh={refreshProviders}
            t={t}
          /> : null}
          {section === "operations" ? <section className="panel"><div className="panel-header"><div><span className="section-kicker">AUDITED OPERATIONS</span><h2>{t("最近 LLM Gateway 操作", "Recent LLM Gateway operations")}</h2></div></div>{operations.length === 0 ? <div className="administrator-loading">{t("尚无 LLM Gateway 操作。", "No LLM Gateway operations yet.")}</div> : <div className="administrator-table-wrap"><table className="administrator-table"><thead><tr><th>{t("目标", "Target")}</th><th>{t("操作", "Action")}</th><th>{t("原因", "Reason")}</th><th>{t("结果", "Result")}</th><th>{t("时间", "Time")}</th></tr></thead><tbody>{operations.map((operation) => <tr key={operation.operation_id}><td>{operation.target_id}</td><td>{operation.action}</td><td>{operation.reason}</td><td><span className={`user-state user-state-${operation.state === "succeeded" ? "active" : "disabled"}`}>{operation.state}</span></td><td>{formatDateTime(operation.created_at)}</td></tr>)}</tbody></table></div>}</section> : null}
        </>
      )}
    </div>
  );
}

function AdministratorsPage() {
  const { t } = useI18n();
  const { session } = useDashboard();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [input, setInput] = useState<AdminUserInput>({ username: "", display_name: "", password: "" });
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [resetTarget, setResetTarget] = useState<AdminUser | null>(null);
  const [resetPassword, setResetPassword] = useState("");
  const [resetConfirmation, setResetConfirmation] = useState("");

  const refreshUsers = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setUsers(await listAdminUsers());
    } catch (caught) {
      setError(authenticationError(caught, t));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshUsers();
  }, [refreshUsers]);

  async function createUser(event: FormEvent) {
    event.preventDefault();
    if (!input.username.trim() || !input.display_name.trim() || !input.password) {
      setError(t("请完整填写用户名、显示名称和临时密码", "Complete the username, display name, and temporary password"));
      return;
    }
    if (input.password !== passwordConfirmation) {
      setError(t("两次输入的临时密码不一致", "The temporary passwords do not match"));
      return;
    }
    setSubmitting(true);
    setError("");
    setNotice("");
    try {
      const created = await createAdminUser(session.csrf_token, {
        ...input,
        username: input.username.trim(),
        display_name: input.display_name.trim()
      });
      setInput({ username: "", display_name: "", password: "" });
      setPasswordConfirmation("");
      setNotice(t(`已创建 ${created.display_name}；首次登录必须修改临时密码。`, `${created.display_name} was created and must change the temporary password at first sign-in.`));
      await refreshUsers();
    } catch (caught) {
      setError(authenticationError(caught, t));
    } finally {
      setSubmitting(false);
    }
  }

  async function toggleUser(user: AdminUser) {
    setSubmitting(true);
    setError("");
    setNotice("");
    try {
      const nextState = user.state === "active" ? "disabled" : "active";
      await setAdminUserState(session.csrf_token, user.admin_user_id, nextState);
      setNotice(t(
        `${user.display_name} 已${nextState === "active" ? "启用" : "停用"}。`,
        `${user.display_name} was ${nextState === "active" ? "enabled" : "disabled"}.`
      ));
      await refreshUsers();
    } catch (caught) {
      setError(authenticationError(caught, t));
    } finally {
      setSubmitting(false);
    }
  }

  async function submitPasswordReset(event: FormEvent) {
    event.preventDefault();
    if (!resetTarget) return;
    if (resetPassword !== resetConfirmation) {
      setError(t("两次输入的临时密码不一致", "The temporary passwords do not match"));
      return;
    }
    setSubmitting(true);
    setError("");
    setNotice("");
    try {
      await resetAdminUserPassword(session.csrf_token, resetTarget.admin_user_id, resetPassword);
      setNotice(t(`${resetTarget.display_name} 的密码已重置；其现有会话已失效。`, `${resetTarget.display_name}'s password was reset and existing sessions were invalidated.`));
      setResetTarget(null);
      setResetPassword("");
      setResetConfirmation("");
      await refreshUsers();
    } catch (caught) {
      setError(authenticationError(caught, t));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="CONTROL CENTER IDENTITY"
        title={t("管理员", "Administrators")}
        description={t("管理员账号属于当前数据中心 Control Center。首版不区分角色，身份用于登录、操作归属和审计。", "Administrator accounts belong to this data center's Control Center. The first release has one role; identity is used for sign-in, operation ownership, and audit.")}
        status={<span className="read-only-label">{users.filter((user) => user.state === "active").length} active</span>}
      />
      {error ? <div className="global-error administrator-message" role="alert">{error}</div> : null}
      {notice ? <div className="success-message" role="status">{notice}</div> : null}
      <section className="administrator-layout">
        <div className="panel administrator-list-panel">
          <div className="panel-header">
            <div>
              <span className="section-kicker">ADMINISTRATORS</span>
              <h2>{t("Control Center 用户", "Control Center users")}</h2>
            </div>
            <button className="secondary-button" type="button" onClick={refreshUsers} disabled={loading || submitting}>{t("刷新", "Refresh")}</button>
          </div>
          {loading ? <div className="administrator-loading">{t("正在读取管理员…", "Loading administrators…")}</div> : (
            <div className="administrator-table-wrap">
              <table className="administrator-table">
                <thead>
                  <tr><th>{t("管理员", "Administrator")}</th><th>{t("状态", "Status")}</th><th>{t("最后登录", "Last sign-in")}</th><th><span className="visually-hidden">{t("操作", "Actions")}</span></th></tr>
                </thead>
                <tbody>
                  {users.map((user) => {
                    const isCurrentUser = user.admin_user_id === session.user.admin_user_id;
                    return (
                      <tr key={user.admin_user_id}>
                        <td>
                          <strong>{user.display_name}</strong>
                          <span>@{user.username}{isCurrentUser ? t(" · 当前账号", " · current account") : ""}</span>
                        </td>
                        <td>
                          <span className={`user-state user-state-${user.state}`}>{user.state === "active" ? t("已启用", "Enabled") : t("已停用", "Disabled")}</span>
                          {user.must_change_password ? <small>{t("待修改临时密码", "Temporary password change required")}</small> : null}
                        </td>
                        <td>{user.last_login_at ? formatDateTime(user.last_login_at) : t("尚未登录", "Never signed in")}</td>
                        <td>
                          <div className="table-actions">
                            <button type="button" onClick={() => setResetTarget(user)} disabled={submitting || isCurrentUser}>{t("重置密码", "Reset password")}</button>
                            <button type="button" onClick={() => toggleUser(user)} disabled={submitting || isCurrentUser}>
                              {user.state === "active" ? t("停用", "Disable") : t("启用", "Enable")}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <aside className="panel administrator-create-panel">
          <span className="section-kicker">CREATE ADMINISTRATOR</span>
          <h2>{t("添加管理员", "Add administrator")}</h2>
          <p>{t("新管理员使用临时密码首次登录，并在进入后台前设置自己的正式密码。", "New administrators sign in with a temporary password and set a permanent password before entering the control surface.")}</p>
          <form onSubmit={createUser}>
            <label htmlFor="new-admin-username">{t("用户名", "Username")}</label>
            <input id="new-admin-username" value={input.username} onChange={(event) => setInput({ ...input, username: event.target.value })} autoComplete="off" spellCheck={false} />
            <label htmlFor="new-admin-display-name">{t("显示名称", "Display name")}</label>
            <input id="new-admin-display-name" value={input.display_name} onChange={(event) => setInput({ ...input, display_name: event.target.value })} autoComplete="off" />
            <label htmlFor="new-admin-password">{t("临时密码", "Temporary password")}</label>
            <input id="new-admin-password" type="password" value={input.password} onChange={(event) => setInput({ ...input, password: event.target.value })} autoComplete="new-password" placeholder={t("至少 12 个字符", "At least 12 characters")} />
            <label htmlFor="new-admin-password-confirmation">{t("确认临时密码", "Confirm temporary password")}</label>
            <input id="new-admin-password-confirmation" type="password" value={passwordConfirmation} onChange={(event) => setPasswordConfirmation(event.target.value)} autoComplete="new-password" />
            <button className="primary-button" type="submit" disabled={submitting}>{submitting ? t("正在处理…", "Working…") : t("创建管理员", "Create administrator")}</button>
          </form>
        </aside>
      </section>
      {resetTarget ? (
        <div className="dialog-backdrop" role="presentation">
          <section className="password-reset-dialog" role="dialog" aria-modal="true" aria-labelledby="password-reset-title">
            <span className="section-kicker">RESET PASSWORD</span>
            <h2 id="password-reset-title">{t(`重置 ${resetTarget.display_name} 的密码`, `Reset ${resetTarget.display_name}'s password`)}</h2>
            <p>{t("保存后，该管理员的全部现有会话立即失效；新密码只能作为首次登录的临时密码。", "Saving invalidates all existing sessions for this administrator. The new value is a first-sign-in temporary password.")}</p>
            <form onSubmit={submitPasswordReset}>
              <label htmlFor="reset-password">{t("新临时密码", "New temporary password")}</label>
              <input id="reset-password" type="password" value={resetPassword} onChange={(event) => setResetPassword(event.target.value)} autoComplete="new-password" autoFocus />
              <label htmlFor="reset-password-confirmation">{t("确认临时密码", "Confirm temporary password")}</label>
              <input id="reset-password-confirmation" type="password" value={resetConfirmation} onChange={(event) => setResetConfirmation(event.target.value)} autoComplete="new-password" />
              <div className="dialog-actions">
                <button className="secondary-button" type="button" onClick={() => setResetTarget(null)} disabled={submitting}>{t("取消", "Cancel")}</button>
                <button className="primary-button" type="submit" disabled={submitting}>{submitting ? t("正在保存…", "Saving…") : t("确认重置", "Confirm reset")}</button>
              </div>
            </form>
          </section>
        </div>
      ) : null}
    </div>
  );
}

function ComponentRow({ item }: { item: ComponentObservation }) {
	const { t } = useI18n();
	const target = item.id === "kubernetes" ? "/cluster" : item.id === "ids" ? "/ids" : item.id === "team-placement" ? "/team-runtime" : item.id === "sandbox" ? "/sandbox" : item.id === "llm-gateway" ? "/llm-gateway" : "/";
	const monogram = item.id === "kubernetes" ? "K8" : item.id === "ids" ? "ID" : item.id === "team-placement" ? "TP" : item.id === "sandbox" ? "SB" : "LL";
  return (
    <Link className="component-row" to={target}>
      <div className="component-identity">
        <span className="component-monogram">{monogram}</span>
        <div>
          <strong>{item.name}</strong>
          <span>{item.kind}</span>
        </div>
      </div>
      <div className="component-message">
        <span>{item.message || t("等待组件观测", "Waiting for component observation")}</span>
        <small>{item.error_code ?? `Observed ${formatTime(item.observed_at)}`}</small>
      </div>
      <StatusBadge status={item.status} />
      <span className="row-arrow" aria-hidden="true">→</span>
    </Link>
  );
}

function ComponentDetailPanel({ component }: { component: ComponentObservation }) {
  const { t } = useI18n();
  return (
    <section className="panel component-detail-panel">
      <div className="panel-header">
        <div>
          <span className="section-kicker">LIVE OBSERVATION</span>
          <h2>{component.name}</h2>
        </div>
        <StatusBadge status={component.status} />
      </div>
      <dl className="detail-list">
        <div><dt>Component ID</dt><dd>{component.id}</dd></div>
        <div><dt>Kind</dt><dd>{component.kind}</dd></div>
        <div><dt>Observed</dt><dd>{formatTime(component.observed_at)}</dd></div>
        <div><dt>Error code</dt><dd>{component.error_code ?? "—"}</dd></div>
      </dl>
      <div className={`observation-message observation-${component.status}`}>
        <span className={`status-dot status-dot-${component.status}`} />
        <span>{component.message || t("组件没有返回状态说明", "The component returned no status message")}</span>
      </div>
    </section>
  );
}

function SetupPanel({ title, description, items }: { title: string; description: string; items: string[] }) {
  return (
    <section className="setup-panel">
      <div>
        <span className="setup-label">SETUP REQUIRED</span>
        <h2>{title}</h2>
        <p>{description}</p>
      </div>
      <ol>
        {items.map((item, index) => (
          <li key={item}><span>{String(index + 1).padStart(2, "0")}</span>{item}</li>
        ))}
      </ol>
    </section>
  );
}

function PageHeader({
  eyebrow,
  title,
  description,
  status
}: {
  eyebrow: string;
  title: string;
  description: string;
  status?: ReactNode;
}) {
  return (
    <header className="page-header">
      <div>
        <span className="eyebrow page-eyebrow">{eyebrow}</span>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      {status}
    </header>
  );
}

function PageTabs<T extends string>({
  value,
  onChange,
  items
}: {
  value: T;
  onChange: (value: T) => void;
  items: Array<{ value: T; label: string; count?: number }>;
}) {
  const { t } = useI18n();
  return (
    <div className="page-tabs" role="tablist" aria-label={t("页面内容分区", "Page sections") }>
      {items.map((item) => (
        <button
          key={item.value}
          type="button"
          role="tab"
          aria-selected={value === item.value}
          className={value === item.value ? "page-tab page-tab-active" : "page-tab"}
          onClick={() => onChange(item.value)}
        >
          <span>{item.label}</span>
          {item.count !== undefined ? <small>{item.count}</small> : null}
        </button>
      ))}
    </div>
  );
}

function MetricCard({
  label,
  value,
  note,
  tone
}: {
  label: string;
  value: string;
  note: string;
  tone: "blue" | "green" | "orange" | "neutral";
}) {
  return (
    <article className={`metric-card metric-${tone}`}>
      <span>{label}</span>
      <strong title={value}>{value}</strong>
      <small>{note}</small>
    </article>
  );
}

function StatusBadge({ status, label }: { status: ComponentStatus; label?: string }) {
  const { t } = useI18n();
  return (
    <span className={`status-badge status-${status}`}>
      <span className={`status-dot status-dot-${status}`} />
      {label ?? statusLabel(status, t)}
    </span>
  );
}

function MissingComponent({ title }: { title: string }) {
  const { t } = useI18n();
  return <section className="empty-state"><strong>{title}</strong><span>{t("请检查 Control Center component catalog。", "Check the Control Center component catalog.")}</span></section>;
}

function LoadingState() {
  const { t } = useI18n();
  return (
    <div className="loading-state" role="status">
      <span className="loading-pulse" />
      <strong>{t("正在读取数据中心状态", "Loading data-center status")}</strong>
      <span>{t("Control Center 正在聚合组件观测结果…", "Control Center is aggregating component observations…")}</span>
    </div>
  );
}

function useDashboard(): DashboardContextValue {
  const context = useContext(DashboardContext);
  if (!context) throw new Error("Dashboard context is unavailable");
  return context;
}

function useComponent(id: string): ComponentObservation | undefined {
  const { data } = useDashboard();
  return data?.components.items.find((item) => item.id === id);
}

function detailText(component: ComponentObservation, key: string, fallback: string): string {
  const value = component.details?.[key];
  return typeof value === "string" && value.trim() ? value : fallback;
}

function detailNumber(component: ComponentObservation, key: string): string {
  const value = component.details?.[key];
  return typeof value === "number" ? String(value) : "—";
}

function detailBooleanValue(component: ComponentObservation, key: string): boolean | null {
  const value = component.details?.[key];
  return typeof value === "boolean" ? value : null;
}

function detailBoolean(component: ComponentObservation, key: string, t: Translator): string {
  const value = detailBooleanValue(component, key);
  if (value === null) return "—";
  return value ? t("健康", "Healthy") : t("未就绪", "Not ready");
}

function statusLabel(status: ComponentStatus, t: Translator): string {
  return {
    healthy: t("健康", "Healthy"),
    degraded: t("降级", "Degraded"),
    unavailable: t("不可用", "Unavailable"),
    disabled: t("未配置", "Not configured"),
    unknown: t("未知", "Unknown")
  }[status];
}

function formatTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat(document.documentElement.lang || "zh-CN", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false
  }).format(date);
}

function formatDateTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat(document.documentElement.lang || "zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false
  }).format(date);
}

function formatDuration(seconds: number): string {
	if (!Number.isFinite(seconds) || seconds < 0) return "—";
	if (seconds < 60) return `${Math.round(seconds)}s`;
	if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
	if (seconds < 86400) return `${Math.round(seconds / 3600)}h`;
	return `${Math.round(seconds / 86400)}d`;
}

function authenticationError(caught: unknown, t: Translator): string {
  if (caught instanceof APIError) {
    const messages: Record<string, string> = {
      INVALID_CREDENTIALS: t("用户名或密码不正确", "Incorrect username or password"),
      INVALID_BOOTSTRAP_TOKEN: t("Bootstrap token 无效", "Invalid bootstrap token"),
      BOOTSTRAP_COMPLETE: t("首位管理员已经创建，请返回登录", "The first administrator already exists. Return to sign in."),
      PASSWORD_CHANGE_REQUIRED: t("请先修改临时密码", "Change the temporary password first"),
      SELF_DISABLE_FORBIDDEN: t("不能停用当前登录账号", "You cannot disable the current account"),
      SELF_PASSWORD_RESET_FORBIDDEN: t("当前账号请使用修改密码功能", "Use the change-password flow for the current account"),
      LAST_ADMINISTRATOR: t("不能停用最后一个可用管理员", "The last active administrator cannot be disabled"),
      CONFLICT: t("该管理员用户名已存在", "That administrator username already exists"),
      INVALID_CSRF: t("当前页面会话已变化，请刷新后重试", "The page session changed. Refresh and try again."),
      UNAUTHENTICATED: t("登录会话已失效，请重新登录", "The sign-in session expired. Sign in again.")
    };
    return messages[caught.code] ?? diagnosticErrorText(caught,t);
  }
  if (caught instanceof Error) return t(`无法连接管理服务：${caught.message}`, `Unable to connect to the management service: ${caught.message}`);
  return t("无法连接管理服务", "Unable to connect to the management service");
}

function AccessKeysRoute() { const { session } = useDashboard(); return <AccessKeysPage session={session} />; }
